import { AdminRemovalDialog } from './admin/AdminRemovalDialog';

import { SeasonTicketsCard } from './admin/SeasonTicketsCard';
import { resolveDateRange } from '../components/DateRangeFilter';
import { Skeleton } from '../components/ui/skeleton';

import { stableRecordKey } from '../lib/rowIdentity';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, Ticket, Heart, Bell, QrCode, Upload,
  Plus, Trash2, Eye, Image as ImageIcon, EyeOff as EyeOffIcon, Search, Download, LogOut, Menu, X, Edit, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, FileText, Settings, Mail, Send, RefreshCw, Lock, CreditCard, Receipt, Images, HelpCircle, UserCog, HandHeart, Megaphone, Gift, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { EventPromotePanel } from '../components/promo/EventPromo';
import { FestivalPhotosPanel } from '../components/promo/FestivalPhotosPanel';
import { ComplimentaryPanel } from '../components/comps/ComplimentaryPanel';
import { DateRangeFilter, emptyDateRange, inDateRange, describeDateRange, downloadCsv, fmtDateGB, fmtMoney, rangeSlug } from '../components/DateRangeFilter';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../App';
import {
  NewslettersAdminTab, UsersTab, ActivityTab, ExpensesTab, MinutesTab,
  ApplicationsTab, CameraScannerTab, AssistantTab
} from './admin/AdminModules';
import { VerificationTab, MembershipsTab, DocumentsTab, SponsorsAdminTab } from './admin/MembershipModules';
import { FinalDbGate, MembershipSettingsTab } from './admin/GovernanceModules';
import { SaturdayClubSettingsTab } from './admin/SaturdayClubModules';
import { ContentEditorTab } from './admin/ContentModules';
import { RefundsTab } from './admin/FinanceModules';
import { GalleryAdminTab } from './admin/GalleryModules';
import { MemberImportTab } from './admin/ImportModules';
import { MigrationTab } from './admin/MigrationModules';
import { CommitteeAdminTab, FaqAdminTab } from './admin/PeopleFaqModules';
import { VolunteersAdminTab } from './admin/VolunteerModules';
import { WeeklyDigestCard } from './admin/DigestModules';
import { SesDeliveryCard } from './admin/EmailSesModules';
import { SesTestWorkflowCard } from './admin/SesTestWorkflow';
import { MembersDownloadMenu, MemberEditButton } from './admin/FinalDbModules';
import { dbUnlock } from '../App';
import { MemberOperationsButton, BookingOperationsButton, OperationsLogTab, PrivacyPolicyAdminCard, ComposeEmailTab } from './admin/OperationsModules';

// Sidebar items with role-based access control, grouped per spec P1.1
// (Dashboard / Membership / Saturday Club / Events, Payments & Refunds / Frontend / Settings)
const SIDEBAR_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'treasurer', 'chairman'], group: 'Dashboard' },
  { key: 'compose_email', label: 'Compose Email', icon: Mail, roles: ['admin'], group: 'Dashboard' },
  // Membership
  { key: 'applications', label: 'Applications / Review', icon: FileText, roles: ['admin', 'chairman'], group: 'Membership' },
  { key: 'verification', label: 'Verification Queue', icon: CheckCircle, roles: ['admin', 'chairman'], group: 'Membership' },
  { key: 'memberships', label: 'Final Member Database', icon: Users, roles: ['admin', 'chairman'], group: 'Membership', protected: true },
  { key: 'members', label: 'Members (legacy list)', icon: Users, roles: ['admin', 'chairman'], group: 'Membership', protected: true },
  { key: 'membership_settings', label: 'Pricing & Settings', icon: Settings, roles: ['admin', 'chairman'], group: 'Membership' },
  { key: 'import_review', label: 'Import Review', icon: Upload, roles: ['admin'], group: 'Membership' },
  { key: 'migration', label: 'Existing Member Migration', icon: Users, roles: ['admin'], group: 'Membership' },
  { key: 'late_id', label: 'Late ID Check List', icon: ShieldCheck, roles: ['admin'], group: 'Membership', protected: true },
  // Saturday Club
  { key: 'saturday_club', label: 'Registrations', icon: Users, roles: ['admin', 'treasurer', 'chairman'], group: 'Saturday Club' },
  { key: 'saturday_club_settings', label: 'Club Settings', icon: Settings, roles: ['admin', 'chairman', 'treasurer'], group: 'Saturday Club' },
  { key: 'documents', label: 'Documents', icon: FileText, roles: ['admin', 'chairman'], group: 'Saturday Club' },
  // Events, payments, refunds
  { key: 'events', label: 'Events', icon: Calendar, roles: ['admin'], group: 'Events & Payments' },
  { key: 'tickets', label: 'Tickets', icon: Ticket, roles: ['admin', 'treasurer', 'chairman'], group: 'Events & Payments' },
  { key: 'donations', label: 'Donations', icon: Heart, roles: ['admin', 'treasurer', 'chairman'], group: 'Events & Payments' },
  { key: 'refunds', label: 'Payments & Refunds', icon: Receipt, roles: ['admin', 'treasurer', 'chairman'], group: 'Events & Payments' },
  { key: 'refund_details', label: 'Refund Details', icon: Receipt, roles: ['admin'], group: 'Events & Payments' },
  { key: 'outgoing_email', label: 'Outgoing Email Log', icon: Mail, roles: ['admin'], group: 'Organisation' },
  { key: 'privacy_pdf', label: 'Privacy Policy PDF', icon: FileText, roles: ['admin'], group: 'Settings' },
  { key: 'scanner', label: 'QR Scanner', icon: QrCode, roles: ['admin', 'scanner'], group: 'Events & Payments' },
  // Frontend / content
  { key: 'gallery', label: 'Gallery Albums', icon: Images, roles: ['admin', 'chairman', 'committee', 'treasurer', 'scanner'], group: 'Frontend' },
  { key: 'sponsors', label: 'Sponsors', icon: Heart, roles: ['admin', 'chairman'], group: 'Frontend' },
  { key: 'newsletters', label: 'Newsletters', icon: FileText, roles: ['admin', 'chairman'], group: 'Frontend' },
  { key: 'files', label: 'Media & Files', icon: Upload, roles: ['admin', 'chairman', 'committee', 'treasurer', 'scanner'], group: 'Frontend' },
  { key: 'cms', label: 'Page Content', icon: FileText, roles: ['admin'], group: 'Frontend' },
  { key: 'committee', label: 'Committee & Trustees', icon: UserCog, roles: ['admin'], group: 'Frontend' },
  { key: 'faq', label: 'FAQ', icon: HelpCircle, roles: ['admin'], group: 'Frontend' },
  // Organisation
  { key: 'expenses', label: 'Expenses', icon: FileText, roles: ['admin', 'treasurer', 'chairman', 'committee'], group: 'Organisation' },
  { key: 'volunteers', label: 'Volunteers', icon: HandHeart, roles: ['admin'], group: 'Organisation' },
  { key: 'minutes', label: 'Meeting Minutes', icon: FileText, roles: ['admin', 'treasurer', 'chairman', 'committee'], group: 'Organisation' },
  { key: 'reminders', label: 'Reminders', icon: Bell, roles: ['admin'], group: 'Organisation' },
  { key: 'assistant', label: 'AI Assistant', icon: Bell, roles: ['admin', 'treasurer'], group: 'Organisation' },
  { key: 'data_requests', label: 'Data Requests', icon: FileText, roles: ['admin', 'chairman'], group: 'Organisation' },
  // Settings
  { key: 'users', label: 'Staff Accounts', icon: Users, roles: ['admin'], group: 'Settings' },
  { key: 'activity', label: 'Activity Log', icon: Eye, roles: ['admin'], group: 'Settings' },
  { key: 'settings', label: 'Settings', icon: Settings, roles: ['admin', 'treasurer'], group: 'Settings' },
];
const GROUP_ORDER = ['Dashboard', 'Membership', 'Saturday Club', 'Events & Payments', 'Frontend', 'Organisation', 'Settings'];

// Chairman = oversight only (user decision). Exactly these tabs, nothing else; the server enforces the same list.
const CHAIRMAN_TABS = ['dashboard', 'expenses', 'minutes', 'files', 'gallery', 'saturday_club', 'donations'];
const itemsForRole = (role) => SIDEBAR_ITEMS.filter(item => item.roles.includes(role) && (role !== 'chairman' || CHAIRMAN_TABS.includes(item.key)));

const ROLE_LABELS = { admin: 'Admin', treasurer: 'Treasurer', chairman: 'Chairman', committee: 'Committee' };
const ROLE_BADGE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  treasurer: 'bg-green-100 text-green-800',
  chairman: 'bg-blue-100 text-blue-800',
  committee: 'bg-amber-100 text-amber-800'
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState(localStorage.getItem('gs_admin_token'));
  const role = localStorage.getItem('gs_admin_role') || 'admin';
  const adminName = localStorage.getItem('gs_admin_name') || 'Staff';
  const visibleItems = itemsForRole(role);
  const defaultTab = visibleItems[0]?.key || 'expenses';
  // Support deep-links like /admin?tab=scanner (used by the installed PWA start URL),
  // but only when the requested tab is allowed for this staff role.
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    if (requested && visibleItems.some(item => item.key === requested)) return requested;
    return defaultTab;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    const allowed = itemsForRole(role);
    setActiveTab(allowed.some(item => item.key === requested) ? requested : (allowed[0]?.key || 'expenses'));
  }, [location.search, role]);
  const goToTab = (tab) => {
    if (!itemsForRole(role).some(item => item.key === tab)) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    setActiveTab(tab);
    navigate({ pathname: location.pathname, search: `?${params}` });
  };

  useEffect(() => {
    if (!token) { navigate('/admin/login' + window.location.search); return; }
    // Validate the saved token up-front: an expired one is cleared and the login page shown
    // (handled centrally in api.request), instead of a dashboard full of empty lists.
    api.getAuth('/api/admin/me', token).catch(() => {});
    if (['admin', 'treasurer', 'chairman'].includes(role)) loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadStats = async () => {
    if (role === 'chairman') return;   // chairman dashboard loads its own figures-only overview
    try {
      const data = await api.getAuth('/api/admin/stats', token);
      setStats(data);
    } catch { /* silent for restricted roles */ }
  };

  const handleLogout = () => {
    api.postAuth('/api/admin/final-db/lock', {}, token).catch(() => {});
    dbUnlock.clear();
    localStorage.removeItem('gs_admin_token');
    localStorage.removeItem('gs_admin_name');
    localStorage.removeItem('gs_admin_role');
    navigate('/admin/login');
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[hsl(var(--gs-navy))] text-white transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="admin-sidebar">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--gs-saffron))] flex items-center justify-center text-white font-bold text-xs" style={{fontFamily: 'Space Grotesk'}}>GS</div>
              <span className="font-semibold text-sm" style={{fontFamily: 'Space Grotesk'}}>Staff Portal</span>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden"><X className="w-5 h-5" /></button>
          </div>
          <div className="mt-3 px-1">
            <div className="text-xs text-white/60">{adminName}</div>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${ROLE_BADGE_COLORS[role]}`} data-testid="admin-role-badge">
              {ROLE_LABELS[role] || role}
            </span>
          </div>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-180px)]" aria-label="Staff navigation">
          {GROUP_ORDER.filter(g => visibleItems.some(i => i.group === g)).map(group => (
            <div key={group} className="pb-1">
              {group !== 'Dashboard' && <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/40" data-testid={`admin-nav-group-${group.replace(/[^a-z]/gi, '').toLowerCase()}`}>{group}</div>}
              {visibleItems.filter(i => i.group === group).map(item => (
                <button
                  key={item.key}
                  onClick={() => { goToTab(item.key); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${activeTab === item.key ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                  data-testid={`admin-nav-${item.key}`}
                  aria-current={activeTab === item.key ? 'page' : undefined}
                >
                  <item.icon className="w-4 h-4 shrink-0" /> <span className="flex-1 text-left">{item.label}</span>
                  {item.protected && <Lock className="w-3 h-3 text-white/40" aria-label="Requires database password" />}
                  {item.deferred && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">{item.deferred}</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="absolute bottom-4 left-4 right-4">
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-[hsl(var(--border))] px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden"><Menu className="w-5 h-5" /></button>
            <h1 className="font-semibold" style={{fontFamily: 'Space Grotesk'}}>{SIDEBAR_ITEMS.find(i => i.key === activeTab)?.label || activeTab.replace(/_/g, ' ')}</h1>
            <Badge className={`text-xs ${ROLE_BADGE_COLORS[role]}`}>{ROLE_LABELS[role]}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="ghost" size="sm">View Site</Button></Link>
          </div>
        </header>

        <main className="p-4 pb-20 sm:p-6">
          {['admin', 'treasurer'].includes(role) && (
            <EmailFailureBanner token={token} onGoToSettings={() => goToTab('settings')} activeTab={activeTab} />
          )}
          {activeTab === 'dashboard' && (role === 'chairman' ? <OverviewDashboard token={token} /> : ['admin', 'treasurer'].includes(role) ? <div className="space-y-8"><OverviewDashboard token={token} /><DashboardTab stats={stats} onNavigate={goToTab} /></div> : <DashboardTab stats={stats} onNavigate={goToTab} />)}
          {activeTab === 'events' && <EventsTab token={token} />}
          {activeTab === 'members' && <FinalDbGate token={token} role={role}><MembersTab token={token} role={role} /></FinalDbGate>}
          {activeTab === 'applications' && <ApplicationsTab token={token} role={role} />}
          {activeTab === 'verification' && <VerificationTab token={token} role={role} />}
          {activeTab === 'memberships' && <FinalDbGate token={token} role={role}><MembershipsTab token={token} role={role} /></FinalDbGate>}
          {activeTab === 'membership_settings' && <MembershipSettingsTab token={token} role={role} />}
          {activeTab === 'import_review' && <MemberImportTab token={token} />}
          {activeTab === 'migration' && <MigrationTab token={token} />}
          {activeTab === 'saturday_club' && <SaturdayClubRegistrationsTab token={token} />}
          {activeTab === 'saturday_club_settings' && <SaturdayClubSettingsTab token={token} role={role} />}
          {activeTab === 'cms' && <ContentEditorTab token={token} role={role} />}
          {activeTab === 'committee' && <CommitteeAdminTab token={token} />}
          {activeTab === 'faq' && <FaqAdminTab token={token} />}
          {activeTab === 'volunteers' && <VolunteersAdminTab token={token} />}
          {activeTab === 'refunds' && <RefundsTab token={token} role={role} />}
          {role === 'admin' && activeTab === 'refund_details' && <OperationsLogTab token={token} kind="refunds" />}
          {role === 'admin' && activeTab === 'compose_email' && <ComposeEmailTab token={token} />}
          {role === 'admin' && activeTab === 'outgoing_email' && <OperationsLogTab token={token} kind="emails" />}
          {role === 'admin' && activeTab === 'late_id' && <FinalDbGate token={token} role={role}><OperationsLogTab token={token} kind="late-id" /></FinalDbGate>}
          {role === 'admin' && activeTab === 'privacy_pdf' && <PrivacyPolicyAdminCard token={token} />}
          {activeTab === 'documents' && <DocumentsTab token={token} role={role} />}
          {activeTab === 'gallery' && <GalleryAdminTab token={token} role={role} />}
          {activeTab === 'sponsors' && <SponsorsAdminTab token={token} />}
          {activeTab === 'tickets' && <TicketsTab token={token} role={role} />}
          {activeTab === 'donations' && <DonationsTab token={token} />}
          {activeTab === 'newsletters' && <NewslettersAdminTab token={token} role={role} />}
          {activeTab === 'expenses' && <ExpensesTab token={token} role={role} />}
          {activeTab === 'minutes' && <MinutesTab token={token} role={role} />}
          {activeTab === 'reminders' && <RemindersTab token={token} />}
          {activeTab === 'files' && <FileManagerTab token={token} role={role} />}
          {activeTab === 'scanner' && <CameraScannerTab token={token} role={role} />}
          {activeTab === 'assistant' && <AssistantTab token={token} role={role} />}
          {activeTab === 'users' && <UsersTab token={token} />}
          {activeTab === 'activity' && <ActivityTab token={token} />}
          {activeTab === 'data_requests' && <DataRequestsTab token={token} />}
          {activeTab === 'settings' && <SettingsTab token={token} role={role} />}
        </main>
      </div>
    </div>
  );
}

// Figures-only oversight dashboard (Chairman). No links into tickets/members, no personal data.
function OverviewDashboard({ token }) {
  const [eventId, setEventId] = useState('all');
  const [range, setRange] = useState(emptyDateRange());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const resolvedRange = resolveDateRange(range);
  const rangeFrom = resolvedRange.from || '';
  const rangeTo = resolvedRange.to || '';
  const load = useCallback(async () => {
    setLoading(true); setError('');
    const params = new URLSearchParams({ event_id: eventId });
    if (rangeFrom) params.set('date_from', rangeFrom);
    if (rangeTo) params.set('date_to', rangeTo);
    try { setData(await api.getAuth(`/api/admin/stats/overview?${params}`, token)); }
    catch (e) { setError(e.message || 'Could not load figures'); }
    setLoading(false);
  }, [eventId, rangeFrom, rangeTo, token]);
  useEffect(() => { load(); }, [load]);
  const money = (v) => `£${Number(v || 0).toFixed(2)}`;
  const ev = data?.event || {}; const org = data?.organisation || {};
  const Kpi = ({ label, value, testId }) => (
    <Card data-testid={testId}><CardContent className="p-4"><div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</div><div className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Space Grotesk' }}>{value}</div></CardContent></Card>
  );
  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Overview</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Figures only — choose an event and, if you like, a booking-date range for its ticket numbers and takings (organisation figures below are not date-filtered).</p>
        </div>
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger className="w-full sm:w-72" data-testid="overview-event-filter"><SelectValue placeholder="All events" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {(data?.events || []).map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeFilter value={range} onChange={setRange} label="Booked" testId="overview-date" />
      </div>
      {error && <Card className="border-[hsl(var(--destructive)/0.4)]"><CardContent className="p-4 flex items-center justify-between gap-3 text-sm" data-testid="overview-error"><span>{error}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></CardContent></Card>}
      {loading && !data ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <div key={i} className="h-24 rounded-xl bg-[hsl(var(--muted))] animate-pulse" />)}</div>
      ) : data && (
        <>
          <div>
            <h3 className="text-sm font-medium mb-2" data-testid="overview-event-title">{data.selected_event ? data.selected_event.title : 'All events'}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Tickets issued" value={ev.tickets_issued ?? 0} testId="overview-tickets-issued" />
              <Kpi label="Regular member / non-member (no student or season)" value={`${ev.member_tickets ?? 0} / ${ev.non_member_tickets ?? 0}`} testId="overview-member-split" />
              <Kpi label="Regular Student Tickets" value={ev.student_tickets ?? 0} testId="overview-student-tickets" />
              <Kpi label="Checked in" value={ev.checked_in ?? 0} testId="overview-checked-in" />
              <Kpi label="Paid bookings" value={ev.paid_orders ?? 0} testId="overview-paid-orders" />
              <Kpi label="Ticket takings" value={money(ev.ticket_revenue)} testId="overview-ticket-revenue" />
              <Kpi label="Refunded" value={money(ev.refunded)} testId="overview-refunded" />
              <Kpi label="Net ticket takings" value={money(ev.net_ticket_revenue)} testId="overview-net-revenue" />
            </div>
            <SeasonTicketsCard summary={ev.ticket_summary} prefix="overview-season-tickets" className="mt-3" />
            {ev.by_day?.length > 1 && (
              <Card className="mt-3"><CardContent className="p-4">
                <div className="text-sm font-medium mb-2">By day</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-sm" data-testid="overview-by-day">
                  {ev.by_day.map(d => <div key={d.day} className="rounded-lg border p-2"><div className="text-xs text-[hsl(var(--muted-foreground))]">{d.day}</div><div>{d.tickets} tickets · {d.checked_in} in</div></div>)}
                </div>
              </CardContent></Card>
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium mb-2">Organisation</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Active members" value={org.active_members ?? 0} testId="overview-active-members" />
              <Kpi label="Donations" value={`${money(org.donations_total)} (${org.donations_count ?? 0})`} testId="overview-donations" />
              <Kpi label="Saturday Club (paid / total)" value={`${org.saturday_club_paid ?? 0} / ${org.saturday_club_registrations ?? 0}`} testId="overview-saturday-club" />
              <Kpi label="Expenses recorded" value={money(org.expenses_total)} testId="overview-expenses" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// GDPR Data Requests tab (admin + chairman)
function DataRequestsTab({ token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api.getAuth('/api/admin/data-requests', token);
      setRequests(d.requests || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    setBusy(r.request_id);
    try {
      await api.putAuth(`/api/admin/data-requests/${r.request_id}`, { status }, token);
      toast.success(`Marked ${status.replace('_', ' ')}`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const exportData = async (email, key) => {
    setBusy(key);
    try {
      const data = await api.getAuth(`/api/admin/data-requests/export?email=${encodeURIComponent(email)}`, token);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data-export-${email.replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ready — ${data.total_records} record(s) found for ${email}`);
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const TYPE_LABELS = { access: 'Copy of data', deletion: 'Delete data', correction: 'Correct data', other: 'Other' };
  const STATUS_STYLES = {
    pending: 'bg-amber-100 text-amber-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  const pending = requests.filter(r => r.status === 'pending').length;
  const [exportEmail, setExportEmail] = useState('');

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>GDPR Data Requests ({requests.length})</h2>
        {pending > 0 && <Badge className="bg-amber-100 text-amber-800" data-testid="data-requests-pending-badge">{pending} pending</Badge>}
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
        Under UK GDPR you must respond within <strong>one month</strong> of the request date. Mark each request as you progress it.
      </p>

      {/* One-click data export by email */}
      <Card className="mb-6" data-testid="data-export-card">
        <CardContent className="p-4">
          <Label className="font-semibold">One-Click Data Export</Label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 mb-3">
            Compile everything held about a person — memberships, bookings, tickets, donations, subscriptions,
            messages and emails — into a single downloadable file for answering access requests.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={exportEmail}
              onChange={e => setExportEmail(e.target.value)}
              placeholder="person@example.com"
              className="sm:w-80"
              data-testid="data-export-email-input"
            />
            <Button
              variant="outline"
              disabled={busy === 'manual-export' || !exportEmail.includes('@')}
              onClick={() => exportData(exportEmail.trim(), 'manual-export')}
              data-testid="data-export-button"
            >
              <FileText className="w-4 h-4 mr-2" />
              {busy === 'manual-export' ? 'Compiling...' : 'Export Data'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : requests.length === 0 ? (
        <Card className="text-center p-10" data-testid="data-requests-empty">
          <FileText className="w-10 h-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-[hsl(var(--muted-foreground))]">No data requests received yet.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <Card key={r.request_id} data-testid={`data-request-${r.request_id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className="text-xs">{TYPE_LABELS[r.request_type] || r.request_type}</Badge>
                    <Badge className={`text-xs ${STATUS_STYLES[r.status] || ''}`}>{r.status?.replace('_', ' ')}</Badge>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : ''}</span>
                  </div>
                  <div className="font-medium text-sm">{r.name} <span className="text-[hsl(var(--muted-foreground))]">— {r.email}</span></div>
                  {r.details && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 truncate">{r.details}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => exportData(r.email, r.request_id + '-export')} disabled={busy === r.request_id + '-export'} title="Download everything held about this person" data-testid={`data-request-export-${r.request_id}`}>
                    <FileText className="w-3.5 h-3.5 mr-1" /> Export
                  </Button>
                  {r.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(r, 'in_progress')} disabled={busy === r.request_id} data-testid={`data-request-progress-${r.request_id}`}>Start</Button>
                  )}
                  {r.status !== 'completed' && r.status !== 'rejected' && (
                    <Button size="sm" onClick={() => setStatus(r, 'completed')} disabled={busy === r.request_id} data-testid={`data-request-complete-${r.request_id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Complete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Delivery alert banner: warns staff when several emails have failed to send
function EmailFailureBanner({ token, onGoToSettings, activeTab }) {
  const [failed, setFailed] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const data = await api.getAuth('/api/admin/email-settings', token);
        if (!cancelled) setFailed(data?.queue?.failed ?? 0);
      } catch { /* non-critical */ }
    };
    check();
    const id = setInterval(check, 120000); // re-check every 2 minutes
    return () => { cancelled = true; clearInterval(id); };
  }, [token, activeTab]);

  if (dismissed || failed < 3) return null;

  return (
    <div className="mb-4 flex items-start sm:items-center gap-3 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3" role="alert" data-testid="email-failure-banner">
      <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5 sm:mt-0" />
      <div className="flex-1 text-sm text-red-800">
        <strong>{failed} emails have failed to send.</strong> Tickets or newsletters may not be reaching people — please review and retry.
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100" onClick={onGoToSettings} data-testid="email-failure-banner-review-button">
          Review & Retry
        </Button>
        <button onClick={() => setDismissed(true)} aria-label="Dismiss alert" className="text-red-400 hover:text-red-700" data-testid="email-failure-banner-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Dashboard KPI Cards
function DashboardTab({ stats, onNavigate }) {
  if (!stats) return <div className="animate-pulse space-y-4">{[1,2].map(i => <div key={i} className="h-24 bg-[hsl(var(--muted))] rounded-xl" />)}</div>;
  const kpis = [
    { label: 'Total Members', value: stats.total_members, icon: Users, color: 'gs-saffron', tab: 'members', hint: 'View all members' },
    { label: 'Upcoming Events', value: stats.upcoming_events, icon: Calendar, color: 'ring', tab: 'events', hint: 'Manage events' },
    { label: 'Ticket Revenue', value: `£${stats.ticket_revenue?.toFixed(2) || '0.00'}`, icon: Ticket, color: 'gs-success', tab: 'tickets', hint: 'View ticket orders' },
    { label: 'Donations', value: `£${stats.donation_revenue?.toFixed(2) || '0.00'}`, icon: Heart, color: 'destructive', tab: 'donations', hint: 'View donation details' },
    { label: 'Total Tickets', value: stats.total_tickets, icon: Ticket, color: 'gs-navy', tab: 'tickets', hint: 'View all tickets' },
    { label: 'Member Tickets', value: stats.member_tickets, icon: Users, color: 'gs-saffron', tab: 'tickets', hint: 'Regular only; no student or season', alwaysHint: true },
    { label: 'Non-Member Tickets', value: stats.non_member_tickets, icon: Users, color: 'muted-foreground', tab: 'tickets', hint: 'Regular only; no student or season', alwaysHint: true },
    { label: 'Student Tickets', value: stats.student_tickets ?? 0, icon: Users, color: 'primary', tab: 'tickets', hint: 'Regular student tickets; season excluded', alwaysHint: true },
    { label: 'Complimentary Tickets', value: stats.complimentary_tickets ?? 0, icon: Gift, color: 'gs-accent-gold', tab: 'tickets', hint: 'Sponsors, donors, guests, volunteers & coupons' },
    { label: 'Total Revenue', value: `£${stats.total_revenue?.toFixed(2) || '0.00'}`, icon: FileText, color: 'gs-success', tab: 'refunds', hint: 'Paid tickets + donations · Payments & Refunds', alwaysHint: true },
  ];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-dashboard-kpis">
      {kpis.map((kpi, i) => (
        <Card
          key={stableRecordKey(kpi)}
          onClick={() => onNavigate && onNavigate(kpi.tab)}
          onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onNavigate?.(kpi.tab); } }}
          role="button" tabIndex={0} aria-label={`${kpi.label}: ${kpi.value}. ${kpi.hint}`}
          className="cursor-pointer hover:shadow-md hover:border-[hsl(var(--primary)/0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-transform duration-150 group"
          data-testid={`kpi-card-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[hsl(var(--muted-foreground))]" data-testid={`kpi-label-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>{kpi.label}</span>
              <kpi.icon className="w-4 h-4" style={{color: `hsl(var(--${kpi.color}))`}} />
            </div>
            <div className="text-2xl font-bold tabular-nums" data-testid={`kpi-value-${kpi.label.toLowerCase().replace(/\s/g, '-')}`} style={{fontFamily: 'Space Grotesk'}}>{kpi.value}</div>
            <div className={`text-[11px] text-[hsl(var(--primary))] ${kpi.alwaysHint ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'} transition-opacity flex items-center gap-0.5 mt-1`}>
              {kpi.hint} <ChevronRight className="w-3 h-3" />
            </div>
          </CardContent>
        </Card>
      ))}
      <SeasonTicketsCard summary={stats.ticket_summary} prefix="dashboard-season-tickets" className="sm:col-span-2 lg:col-span-4" />
    </div>
  );
}

// Events Tab — flexible ticket categories + multi-day support
function EventsTab({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const emptyForm = { title: '', description: '', date: '', venue: '', venue_maps_url: '', image_url: '', capacity: 100, show_capacity: false };
  const [form, setForm] = useState(emptyForm);
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [eventDays, setEventDays] = useState([]);
  const [newDay, setNewDay] = useState('');
  const defaultCategories = [
    { id: 'member', name: 'Member', price: '', season_price: '', season_available: true, fixed_day: '', visible: true, requires_member: true, is_student: false },
    { id: 'non_member', name: 'Non-Member', price: '', season_price: '', season_available: true, fixed_day: '', visible: true, requires_member: false, is_student: false },
  ];
  const [categories, setCategories] = useState(defaultCategories);

  const [popup, setPopup] = useState({ event: null, mode: 'auto' });
  const [popupBusy, setPopupBusy] = useState(false);
  const [promoteFor, setPromoteFor] = useState(null);   // event open in the Promote dialog

  const loadEvents = useCallback(async () => {
    try {
      const evtData = await api.get('/api/events');
      setEvents(evtData.events || []);
      const p = await api.get('/api/events/popup').catch(() => null);
      if (p) setPopup(p);
    } catch {} finally { setLoading(false); }
  }, []);

  // Homepage popup: feature a specific event, go back to automatic (next event), or switch the popup off
  const featureInPopup = async (eventId, featured) => {
    setPopupBusy(true);
    try {
      const d = await api.putAuth(`/api/admin/events/${eventId}/popup`, { featured }, token);
      setPopup(d.popup);
      toast.success(featured ? 'This event now shows in the homepage popup' : 'Popup back to automatic (next upcoming event)');
      loadEvents();
    } catch (e) { toast.error(e.message); } finally { setPopupBusy(false); }
  };
  const togglePopup = async (enabled) => {
    setPopupBusy(true);
    try {
      const d = await api.putAuth('/api/admin/events/popup-enabled', { enabled }, token);
      setPopup(d.popup);
      toast.success(d.message);
    } catch (e) { toast.error(e.message); } finally { setPopupBusy(false); }
  };

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const resetForm = () => {
    setForm(emptyForm);
    setIsMultiDay(false);
    setEventDays([]);
    setCategories(defaultCategories);
    setEditEvent(null);
  };

  const addCategory = () => {
    setCategories([...categories, { id: `cat_${Date.now()}`, name: '', price: '', season_price: '', season_available: false, fixed_day: '', visible: true, requires_member: false, is_student: false }]);
  };

  const updateCategory = (i, field, value) => {
    const next = [...categories];
    next[i] = { ...next[i], [field]: value };
    setCategories(next);
  };

  const removeCategory = (i) => setCategories(categories.filter((_, idx) => idx !== i));

  const addDay = () => {
    if (!newDay) return;
    if (eventDays.includes(newDay)) { toast.error('Day already added'); return; }
    setEventDays([...eventDays, newDay].sort());
    setNewDay('');
  };

  const handleSave = async () => {
    if (!form.title || !form.date) { toast.error('Title and date are required'); return; }
    const validCategories = categories
      .filter(c => c.name.trim() && parseFloat(c.price) > 0)
      .map(c => ({
        id: c.id,
        name: c.name.trim(),
        price: parseFloat(c.price),
        season_available: isMultiDay && !!c.season_available && !c.fixed_day,
        season_price: (isMultiDay && c.season_available && !c.fixed_day && c.season_price) ? parseFloat(c.season_price) : null,
        fixed_day: (isMultiDay && c.fixed_day) ? c.fixed_day : null,
        visible: c.visible !== false,
        requires_member: !!c.requires_member,
        is_student: !!c.is_student
      }));
    if (validCategories.length === 0) { toast.error('Add at least one ticket category with a name and price'); return; }
    const badSeason = validCategories.find(c => c.season_available && !(c.season_price > 0));
    if (badSeason) { toast.error(`Enter a season price for "${badSeason.name}" or untick "Season ticket available"`); return; }
    const badFixed = validCategories.find(c => c.fixed_day && !eventDays.includes(c.fixed_day));
    if (badFixed) { toast.error(`"${badFixed.name}" is set to a day that is not in the event days list`); return; }
    if (isMultiDay && eventDays.length < 2) { toast.error('Multi-day events need at least 2 days'); return; }

    // For single-day events, days list = the event date (so per-day QR logic still works)
    const days = isMultiDay ? eventDays : [form.date.slice(0, 10)];

    const payload = {
      ...form,
      capacity: parseInt(form.capacity) || 100,
      show_capacity: !!form.show_capacity,
      ticket_types: {},
      is_multi_day: isMultiDay,
      event_days: days,
      ticket_categories: validCategories
    };
    try {
      if (editEvent) {
        await api.putAuth(`/api/admin/events/${editEvent.event_id}`, payload, token);
        toast.success('Event updated');
      } else {
        await api.postAuth('/api/admin/events', payload, token);
        toast.success('Event created');
      }
      setShowCreate(false);
      resetForm();
      loadEvents();
    } catch (e) { toast.error(e.message); }
  };

  const handleEdit = (event) => {
    setEditEvent(event);
    setForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? event.date.slice(0, 16) : '',
      venue: event.venue || '',
      venue_maps_url: event.venue_maps_url || '',
      image_url: event.image_url || '',
      capacity: event.capacity || 100,
      show_capacity: !!event.show_capacity,
    });
    setIsMultiDay(!!event.is_multi_day);
    setEventDays(event.event_days || []);
    if (event.ticket_categories?.length) {
      setCategories(event.ticket_categories.map(c => ({
        id: c.id, name: c.name, price: c.price?.toString() || '',
        season_price: c.season_price?.toString() || '', visible: c.visible !== false,
        season_available: c.season_available !== undefined ? !!c.season_available : (parseFloat(c.season_price) > 0),
        fixed_day: c.fixed_day || '',
        requires_member: !!c.requires_member, is_student: !!c.is_student
      })));
    } else if (event.ticket_types && Object.keys(event.ticket_types).length) {
      // Convert legacy ticket types to categories for editing
      setCategories(Object.entries(event.ticket_types).map(([key, info]) => ({
        id: key, name: info.label || key.replace('_', ' '), price: info.price?.toString() || '',
        season_price: '', visible: true
      })));
    } else {
      setCategories(defaultCategories);
    }
    setShowCreate(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.deleteAuth(`/api/admin/events/${eventId}`, token);
      toast.success('Event deleted');
      loadEvents();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>Events Management</h2>
        <Button onClick={() => { resetForm(); setShowCreate(true); }} className="bg-[hsl(var(--primary))] text-white" data-testid="admin-create-event-button">
          <Plus className="w-4 h-4 mr-1" /> Create Event
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-6">
          <CardHeader><CardTitle style={{fontFamily: 'Space Grotesk'}}>{editEvent ? 'Edit Event' : 'Create New Event'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Event title" data-testid="admin-event-title-input" /></div>
              <div><Label>Start Date & Time *</Label><Input type="datetime-local" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="admin-event-date-input" /></div>
            </div>
            <div><Label>Description</Label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Event description" rows={3} className="w-full rounded-lg border border-[hsl(var(--input))] bg-transparent px-3 py-2 text-sm" data-testid="admin-event-description-input" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Venue</Label><Input value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} placeholder="Venue name" data-testid="admin-event-venue-input" /></div>
              <div><Label>Google Maps URL</Label><Input value={form.venue_maps_url} onChange={e => setForm({...form, venue_maps_url: e.target.value})} placeholder="https://maps.google.com/..." /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Image URL</Label><Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." /></div>
              <div><Label>Capacity (total tickets)</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: e.target.value})} data-testid="admin-event-capacity-input" /></div>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
              <input type="checkbox" className="mt-0.5" checked={!!form.show_capacity} onChange={e => setForm({...form, show_capacity: e.target.checked})} data-testid="admin-event-show-capacity" />
              <span><span className="font-medium">Show capacity &amp; remaining tickets on the website</span><br />
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Off by default. Tick it close to the event (e.g. the day before) to show "Remaining: 27" and create urgency. Capacity is always enforced at checkout either way.</span></span>
            </label>

            <Separator />

            {/* Multi-day toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
              <div>
                <Label className="font-semibold">Multi-Day Event (e.g. Navratri)</Label>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Attendees can book selected days or a full season ticket. One QR per attendee per day.</p>
              </div>
              <button onClick={() => setIsMultiDay(!isMultiDay)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isMultiDay ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
                data-testid="admin-event-multiday-toggle">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isMultiDay ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {isMultiDay && (
              <div className="p-4 rounded-lg border border-[hsl(var(--border))] space-y-3">
                <Label className="font-medium">Event Days ({eventDays.length})</Label>
                <div className="flex flex-wrap gap-2">
                  {eventDays.map(day => (
                    <Badge key={day} variant="secondary" className="text-xs flex items-center gap-1">
                      {new Date(day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      <button onClick={() => setEventDays(eventDays.filter(d => d !== day))} className="ml-1 hover:text-red-600" data-testid={`admin-event-remove-day-${day}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  {eventDays.length === 0 && <span className="text-xs text-[hsl(var(--muted-foreground))]">No days added yet</span>}
                </div>
                <div className="flex gap-2">
                  <Input type="date" value={newDay} onChange={e => setNewDay(e.target.value)} className="w-44" data-testid="admin-event-day-input" />
                  <Button variant="outline" size="sm" onClick={addDay} data-testid="admin-event-add-day-button"><Plus className="w-3 h-3 mr-1" /> Add Day</Button>
                </div>
              </div>
            )}

            <Separator />

            {/* Flexible ticket categories */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Ticket Categories</Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Add, rename, price, hide/show categories. {isMultiDay && 'Tick "Season ticket available" only for passes covering all days; use "Valid on one day only" for single-day items such as Aatham Aarti (one QR, no day selection).'}</p>
                </div>
                <Button variant="outline" size="sm" onClick={addCategory} data-testid="admin-event-add-category-button"><Plus className="w-3 h-3 mr-1" /> Add Category</Button>
              </div>
              <div className="space-y-2">
                {categories.map((cat, i) => (
                  <div key={cat.id} className={`flex flex-wrap items-center gap-2 p-3 rounded-lg border ${cat.visible ? 'border-[hsl(var(--border))]' : 'border-dashed border-[hsl(var(--border))] opacity-60'}`}>
                    <Input value={cat.name} onChange={e => updateCategory(i, 'name', e.target.value)} placeholder="Category name (e.g. Member, VIP)" className="flex-1 min-w-[140px]" data-testid={`admin-category-name-${i}`} />
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{isMultiDay ? '£/day' : '£'}</span>
                      <Input type="number" step="0.01" min="0" value={cat.price} onChange={e => updateCategory(i, 'price', e.target.value)} placeholder="0.00" className="w-24" data-testid={`admin-category-price-${i}`} />
                    </div>
                    {isMultiDay && cat.season_available && !cat.fixed_day && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">Season £</span>
                        <Input type="number" step="0.01" min="0" value={cat.season_price} onChange={e => updateCategory(i, 'season_price', e.target.value)} placeholder="0.00" className="w-24" data-testid={`admin-category-season-${i}`} />
                      </div>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => updateCategory(i, 'visible', !cat.visible)} title={cat.visible ? 'Hide category' : 'Show category'} data-testid={`admin-category-toggle-${i}`}>
                      {cat.visible ? <Eye className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[hsl(var(--destructive))]" onClick={() => removeCategory(i)} data-testid={`admin-category-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                    <div className="w-full flex flex-wrap items-center gap-4 pt-1">
                      {isMultiDay && (
                        <>
                          <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${cat.fixed_day ? 'opacity-50' : ''}`} title={cat.fixed_day ? 'A single-day ticket cannot be a season ticket' : ''}>
                            <input type="checkbox" checked={!!cat.season_available && !cat.fixed_day} disabled={!!cat.fixed_day} onChange={e => updateCategory(i, 'season_available', e.target.checked)} data-testid={`admin-category-season-available-${i}`} />
                            Season ticket available (all {eventDays.length} days)
                          </label>
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <span>Valid on one day only:</span>
                            <select value={cat.fixed_day || ''} onChange={e => updateCategory(i, 'fixed_day', e.target.value)}
                              className="rounded-md border border-[hsl(var(--input))] px-2 py-1 text-xs bg-transparent" data-testid={`admin-category-fixed-day-${i}`}>
                              <option value="">No — buyer picks day(s)</option>
                              {eventDays.map(d => <option key={d} value={d}>{new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</option>)}
                            </select>
                          </label>
                        </>
                      )}
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!cat.requires_member} onChange={e => updateCategory(i, 'requires_member', e.target.checked)} data-testid={`admin-category-members-only-${i}`} />
                        Members only (needs verification)
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!cat.is_student} onChange={e => updateCategory(i, 'is_student', e.target.checked)} data-testid={`admin-category-student-${i}`} />
                        Student (asks ID + expiry)
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} className="bg-[hsl(var(--primary))] text-white" data-testid="admin-event-save-button">{editEvent ? 'Update Event' : 'Create Event'}</Button>
              <Button variant="ghost" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <EventPromotePanel event={promoteFor} token={token} open={!!promoteFor} onClose={() => setPromoteFor(null)} onChanged={loadEvents} />

      {/* Homepage popup control */}
      <Card className="mb-6 border-[hsl(var(--primary)/0.3)]" data-testid="event-popup-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold flex items-center gap-2" style={{fontFamily: 'Space Grotesk'}}><Megaphone className="w-4 h-4" /> Homepage "Upcoming event" popup</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="event-popup-status">
              {popup.mode === 'off' && 'Switched off — visitors see no popup.'}
              {popup.mode === 'none' && 'On, but there is no upcoming active event to show yet.'}
              {popup.mode === 'auto' && popup.event && <>Automatic: showing the next event, <strong>{popup.event.title}</strong>. Use "Show in popup" on any event below to feature it instead.</>}
              {popup.mode === 'featured' && popup.event && <>Featured: <strong>{popup.event.title}</strong>. Visitors see this one until you change it or it passes.</>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {popup.mode === 'featured' && popup.event && (
              <Button size="sm" variant="outline" disabled={popupBusy} onClick={() => featureInPopup(popup.event.event_id, false)} data-testid="event-popup-auto-button">Back to automatic</Button>
            )}
            {popup.mode === 'off' ? (
              <Button size="sm" disabled={popupBusy} onClick={() => togglePopup(true)} className="bg-[hsl(var(--primary))] text-white" data-testid="event-popup-on-button">Switch popup on</Button>
            ) : (
              <Button size="sm" variant="outline" disabled={popupBusy} onClick={() => togglePopup(false)} data-testid="event-popup-off-button">Switch popup off</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="space-y-3">
          {events.length === 0 ? (
            <Card className="text-center p-8"><p className="text-[hsl(var(--muted-foreground))]">No events yet. Create your first event!</p></Card>
          ) : events.map(event => (
            <Card key={event.event_id || event._id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold" style={{fontFamily: 'Space Grotesk'}}>{event.title}</h3>
                    <Badge variant={new Date(event.date) > new Date() ? 'default' : 'secondary'} className="text-xs">
                      {new Date(event.date) > new Date() ? 'Upcoming' : 'Past'}
                    </Badge>
                    {event.is_multi_day && <Badge variant="outline" className="text-xs">{event.event_days?.length || 0} days</Badge>}
                    {event.flyer_url && <Badge variant="outline" className="text-xs" data-testid={`admin-event-flyer-badge-${event.event_id}`}><ImageIcon className="w-3 h-3 mr-1" /> Flyer</Badge>}
                    {popup.event?.event_id === event.event_id && popup.mode !== 'off' && (
                      <Badge className="text-xs bg-[hsl(var(--gs-saffron,var(--primary)))] text-white" data-testid={`admin-event-popup-badge-${event.event_id}`}>
                        <Megaphone className="w-3 h-3 mr-1" /> In popup{popup.mode === 'auto' ? ' (auto)' : ''}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))] flex flex-wrap gap-3 mt-1">
                    <span>{new Date(event.date).toLocaleDateString('en-GB')}</span>
                    {event.venue && <span>{event.venue}</span>}
                    <span>Tickets: {event.tickets_sold || 0}/{event.capacity}</span>
                    {event.ticket_categories?.length > 0 && <span>{event.ticket_categories.length} categories</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {new Date(event.date) > new Date() && event.status === 'active' && (
                    event.featured_popup ? (
                      <Button size="sm" variant="outline" disabled={popupBusy} onClick={() => featureInPopup(event.event_id, false)} data-testid={`admin-event-popup-remove-${event.event_id}`}>
                        <Megaphone className="w-3 h-3 mr-1" /> Remove from popup
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={popupBusy} onClick={() => featureInPopup(event.event_id, true)} data-testid={`admin-event-popup-feature-${event.event_id}`}>
                        <Megaphone className="w-3 h-3 mr-1" /> Show in popup
                      </Button>
                    )
                  )}
                  {event.status !== 'demo' && (
                    <Button size="sm" variant={event.flyer_url ? 'outline' : 'default'} className={event.flyer_url ? '' : 'bg-[hsl(var(--primary))] text-white'} onClick={() => setPromoteFor(event)} data-testid={`admin-event-promote-${event.event_id}`}>
                      <Megaphone className="w-3 h-3 mr-1" /> {event.flyer_url ? 'Promote' : 'Add flyer & promote'}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleEdit(event)} data-testid={`admin-edit-event-${event.event_id}`}><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="text-[hsl(var(--destructive))]" onClick={() => handleDelete(event.event_id)} data-testid={`admin-delete-event-${event.event_id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Members Tab
function MembersTab({ token, role }) {
  const isAdmin = role === 'admin';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all'); // all | expiring | milestones
  const [expiring, setExpiring] = useState([]);
  const [sendingRenewal, setSendingRenewal] = useState('');
  const [milestones, setMilestones] = useState({ turned_18: [], turned_65: [] });
  const [sendingMilestone, setSendingMilestone] = useState('');


  const loadExpiring = useCallback(async () => {
    try {
      const d = await api.getAuth('/api/admin/members/expiring', token);
      setExpiring(d.members || []);
    } catch { /* silent */ }
  }, [token]);

  const loadMilestones = useCallback(async () => {
    try {
      const d = await api.getAuth('/api/admin/members/milestones', token);
      setMilestones({ turned_18: d.turned_18 || [], turned_65: d.turned_65 || [] });
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { if (view === 'expiring') loadExpiring(); if (view === 'milestones') loadMilestones(); }, [view, loadExpiring, loadMilestones]);

  const handleSendMilestone = async (person) => {
    setSendingMilestone(person.email);
    try {
      const res = await api.postAuth('/api/admin/members/milestones/send-email', {
        milestone: person.milestone, email: person.email, name: person.name
      }, token);
      toast.success(res.message);
      loadMilestones();
    } catch (e) { toast.error(e.message); }
    setSendingMilestone('');
  };



  const handleSendRenewal = async (m) => {
    setSendingRenewal(m.membership_number);
    try {
      const res = await api.postAuth(`/api/admin/members/${m.membership_number}/send-renewal`, {}, token);
      toast.success(res.message);
      loadExpiring();
    } catch (e) { toast.error(e.message); }
    setSendingRenewal('');
  };

  const loadMembers = useCallback(async () => {
    try {
      const data = await api.getAuth(`/api/admin/members?search=${search}`, token);
      setMembers(data.members || []);
    } catch {} finally { setLoading(false); }
  }, [token, search]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleDelete = async (membershipNumber) => {
    if (!window.confirm('Delete this member?')) return;
    try {
      await api.deleteAuth(`/api/admin/members/${membershipNumber}`, token);
      toast.success('Member deleted');
      loadMembers();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>Members ({members.length})</h2>
          <div className="flex rounded-lg border border-[hsl(var(--border))] overflow-hidden">
            <button onClick={() => setView('all')} className={`px-3 py-1.5 text-xs font-medium ${view === 'all' ? 'bg-[hsl(var(--primary))] text-white' : 'bg-transparent'}`} data-testid="members-view-all">All</button>
            <button onClick={() => setView('expiring')} className={`px-3 py-1.5 text-xs font-medium ${view === 'expiring' ? 'bg-[hsl(var(--primary))] text-white' : 'bg-transparent'}`} data-testid="members-view-expiring">Expiring Soon</button>
            <button onClick={() => setView('milestones')} className={`px-3 py-1.5 text-xs font-medium ${view === 'milestones' ? 'bg-[hsl(var(--primary))] text-white' : 'bg-transparent'}`} data-testid="members-view-milestones">Milestones</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 min-w-0">

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <Input placeholder="Search members..." className="pl-10 w-60" value={search} onChange={e => setSearch(e.target.value)} data-testid="admin-members-search-input" />
          </div>
          <Link to="/admin?tab=import_review"><Button variant="outline" size="sm" data-testid="admin-members-import-link"><Upload className="w-4 h-4 mr-1" /> Import CSV / Excel</Button></Link>
          {isAdmin && <MembersDownloadMenu token={token} search={search} />}
        </div>
      </div>

      {view === 'milestones' ? (
        <div className="space-y-6" data-testid="milestones-view">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            People from member families who have reached key ages. <strong>18+</strong> can be invited to join as members in their own right;
            <strong> 65+</strong> qualify for a complimentary lifetime membership.
          </p>
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="milestones-18-heading">
              <Badge className="bg-blue-100 text-blue-800">18+</Badge> Invite to join membership ({milestones.turned_18.length})
            </h3>
            {milestones.turned_18.length === 0 ? (
              <Card className="text-center p-6"><p className="text-sm text-[hsl(var(--muted-foreground))]">No family members aged 18+ without their own membership yet. Family details are added via membership applications or the Update Details campaign.</p></Card>
            ) : milestones.turned_18.map((p, i) => (
              <Card key={stableRecordKey(p)} className="mb-2" data-testid={`milestone-18-row-${i}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{p.name}</span>
                      <Badge variant="secondary" className="text-xs">Age {p.age}</Badge>
                      {p.parent_name && <span className="text-xs text-[hsl(var(--muted-foreground))]">family of {p.parent_name} ({p.membership_number})</span>}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {p.email || 'no email'} · DOB {p.dob}
                      {p.email_sent_at && <> · <span className="text-green-700">invite sent {new Date(p.email_sent_at).toLocaleDateString('en-GB')}</span></>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleSendMilestone(p)} disabled={sendingMilestone === p.email || !p.email} data-testid={`send-milestone-18-${i}`}>
                    <Mail className="w-3.5 h-3.5 mr-1" />
                    {sendingMilestone === p.email ? 'Sending...' : (p.email_sent_at ? 'Send Again' : 'Send Membership Invite')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="milestones-65-heading">
              <Badge className="bg-amber-100 text-amber-800">65+</Badge> Eligible for Age 65+ lifetime membership at the configured price ({milestones.turned_65.length})
            </h3>
            {milestones.turned_65.length === 0 ? (
              <Card className="text-center p-6"><p className="text-sm text-[hsl(var(--muted-foreground))]">No members or family members aged 65+ awaiting a lifetime offer.</p></Card>
            ) : milestones.turned_65.map((p, i) => (
              <Card key={stableRecordKey(p)} className="mb-2" data-testid={`milestone-65-row-${i}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{p.name}</span>
                      <Badge variant="secondary" className="text-xs">Age {p.age}</Badge>
                      {p.relation === 'member'
                        ? <Badge variant="outline" className="text-xs font-mono">{p.membership_number}</Badge>
                        : (p.parent_name && <span className="text-xs text-[hsl(var(--muted-foreground))]">family of {p.parent_name} ({p.membership_number})</span>)}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {p.email || 'no email'} · DOB {p.dob}
                      {p.email_sent_at && <> · <span className="text-green-700">offer sent {new Date(p.email_sent_at).toLocaleDateString('en-GB')}</span></>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleSendMilestone(p)} disabled={sendingMilestone === p.email || !p.email} data-testid={`send-milestone-65-${i}`}>
                    <Mail className="w-3.5 h-3.5 mr-1" />
                    {sendingMilestone === p.email ? 'Sending...' : (p.email_sent_at ? 'Send Again' : 'Send Lifetime Offer')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : view === 'expiring' ? (
        <div className="space-y-3" data-testid="expiring-members-list">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Standard memberships last <strong>2 years from payment</strong>. Members expiring soonest appear first — the renewal window opens 1 month before expiry.
            Renewing members keep their details; updated ID is only needed if their address changed.
          </p>
          {expiring.length === 0 ? (
            <Card className="text-center p-8"><p className="text-[hsl(var(--muted-foreground))]">No memberships with expiry dates yet.</p></Card>
          ) : expiring.map(m => (
            <Card key={m.membership_number} data-testid={`expiring-member-${m.membership_number}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{m.full_name}</span>
                    <Badge variant="outline" className="text-xs font-mono">{m.membership_number}</Badge>
                    {m.expired ? (
                      <Badge className="bg-red-100 text-red-800 text-xs">Expired {Math.abs(m.days_left)} days ago</Badge>
                    ) : m.renewal_due ? (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">Renewal due — {m.days_left} days left</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">{m.days_left} days left</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {m.email || 'no email'} · expires {m.expires_at ? new Date(m.expires_at).toLocaleDateString('en-GB') : '—'}
                    {m.renewal_email_sent_at && <> · renewal email sent {new Date(m.renewal_email_sent_at).toLocaleDateString('en-GB')}</>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleSendRenewal(m)} disabled={sendingRenewal === m.membership_number || !m.email} data-testid={`send-renewal-${m.membership_number}`}>
                  <Mail className="w-3.5 h-3.5 mr-1" />
                  {sendingRenewal === m.membership_number ? 'Sending...' : 'Send Renewal Email'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm gsn-operations-table" data-testid="legacy-members-table">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Name</th>
                <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Membership #</th>
                <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))] hidden sm:table-cell">Postcode</th>
                <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))] hidden md:table-cell">Email</th>
                <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.membership_number || m._id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
                  <td className="p-3 font-medium">
                    {m.full_name}
                    {(m.family_members?.length > 0) && <Badge variant="secondary" className="ml-2 text-[10px]">+{m.family_members.length} family</Badge>}
                    {m.details_updated_at && <Badge variant="outline" className="ml-1 text-[10px] text-green-700 border-green-300">updated</Badge>}
                  </td>
                  <td className="p-3 font-mono text-xs">{m.membership_number}</td>
                  <td className="p-3 hidden sm:table-cell">{m.postcode}</td>
                  <td className="p-3 hidden md:table-cell text-[hsl(var(--muted-foreground))]">{m.email}</td>
                  <td className="p-3"><Badge variant={m.status === 'active' ? 'default' : 'secondary'} className="text-xs">{m.status}</Badge></td>
                  <td className="p-3 whitespace-nowrap">{isAdmin && <><MemberOperationsButton token={token} membershipNumber={m.membership_number} /><MemberEditButton token={token} membershipNumber={m.membership_number} onSaved={loadMembers} /></>}<Button size="sm" variant="ghost" className="text-[hsl(var(--destructive))]" onClick={() => handleDelete(m.membership_number)}><Trash2 className="w-3 h-3" /></Button></td>
                </tr>
              ))}
              {members.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No members found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Tickets Tab
function TicketsTab({ token, role }) {
  const [tickets, setTickets] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [otherTicket, setOtherTicket] = useState(null); // ticket for "resend to different email" dialog
  const [otherEmail, setOtherEmail] = useState('');
  const [range, setRange] = useState(emptyDateRange());
  const [dateField, setDateField] = useState('created_at');   // booking date | event day
  const shownTickets = tickets.filter(t => inDateRange(dateField === 'event_day' ? (t.event_day ? `${t.event_day}T12:00:00` : null) : t.created_at, range));
  const exportShown = () => downloadCsv(`tickets_${selectedEvent || 'all-events'}_${rangeSlug(range)}`, [
    { key: 'ticket_id', label: 'Ticket ID' }, { key: 'attendee_name', label: 'Name' }, { key: 'ticket_type', label: 'Type' }, { key: 'category_group', label: 'Category group' },
    { key: 'is_member', label: 'Member', format: t => (t.is_member ? 'Yes' : 'No') }, { key: 'membership_number', label: 'Membership No.' },
    { key: 'event_day', label: 'Event day' }, { key: 'status', label: 'Status' }, { key: 'price', label: 'Price (GBP)', format: t => fmtMoney(t.price) },
    { key: 'complimentary', label: 'Complimentary', format: t => (t.complimentary ? 'Yes' : 'No') }, { key: 'comp_reason', label: 'Reason', format: t => t.comp_reason || '' }, { key: 'coupon_code', label: 'Coupon', format: t => t.coupon_code || '' },
    { key: 'customer_email', label: 'Buyer email' }, { key: 'order_id', label: 'Order' }, { key: 'created_at', label: 'Booked', format: t => fmtDateGB(t.created_at) },
  ], shownTickets);

  useEffect(() => {
    api.get('/api/events?include_demo=1').then(d => setEvents(d.events || [])).catch(() => {});
  }, []);

  const [reloadTick, setReloadTick] = useState(0);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const resolvedRange = resolveDateRange(range);
  const summaryFrom = resolvedRange.from || '';
  const summaryTo = resolvedRange.to || '';
  useEffect(() => {
    let cancelled = false;
    setTicketSummary(null); setSummaryError('');
    const params = new URLSearchParams({ date_field: dateField, timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' });
    if (selectedEvent) params.set('event_id', selectedEvent);
    if (summaryFrom) params.set('date_from', summaryFrom);
    if (summaryTo) params.set('date_to', summaryTo);
    api.getAuth(`/api/admin/tickets/summary?${params}`, token)
      .then(result => { if (!cancelled) setTicketSummary(result); })
      .catch(e => { if (!cancelled) setSummaryError(e.message || 'Could not load ticket counts.'); });
    return () => { cancelled = true; };
  }, [token, selectedEvent, dateField, summaryFrom, summaryTo, reloadTick]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setListError('');
    const url = selectedEvent ? `/api/admin/tickets?event_id=${selectedEvent}` : '/api/admin/tickets';
    api.getAuth(url, token).then(d => { if (!cancelled) setTickets(d.tickets || []); })
      .catch(e => { if (!cancelled) setListError(e.message || 'Could not load tickets.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, selectedEvent, reloadTick]);
  const refreshAfterIssue = () => { setReloadTick(t => t + 1); api.get('/api/events?include_demo=1').then(d => setEvents(d.events || [])).catch(() => {}); };

  const [resending, setResending] = useState('');

  const handleResend = async (t, toOverride = null) => {
    if (!t.order_id) { toast.error('No order linked to this ticket'); return; }
    setResending(t.ticket_id);
    try {
      const body = toOverride ? { to: toOverride } : {};
      const res = await api.postAuth(`/api/admin/orders/${t.order_id}/resend-tickets`, body, token);
      toast.success(res.message);
      setOtherTicket(null);
      setOtherEmail('');
    } catch (e) { toast.error(e.message); }
    setResending('');
  };

  const handleExport = async () => {
    if (!selectedEvent) { toast.error('Select an event first'); return; }
    try {
      const data = await api.getAuth(`/api/admin/export/attendees/${selectedEvent}`, token);
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `attendees_${selectedEvent}.csv`; a.click();
    } catch (e) { toast.error(e.message); }
  };

  const [demo, setDemo] = useState(null);
  const loadDemo = () => api.getAuth('/api/admin/demo-tickets', token).then(setDemo).catch(() => {});
  useEffect(() => { loadDemo(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const ensureDemo = async () => { try { const r = await api.postAuth('/api/admin/demo-tickets/ensure', {}, token); toast.success(r.message); loadDemo(); } catch (e) { toast.error(e.message); } };
  const resetDemo = async () => { try { const r = await api.postAuth('/api/admin/demo-tickets/reset', {}, token); toast.success(r.message); loadDemo(); setSelectedEvent(s => s); } catch (e) { toast.error(e.message); } };
  const openSheet = async () => {
    try {
      const res = await api.fetchAuth('/api/admin/demo-tickets/sheet', token);
      if (!res.ok) throw new Error('Could not build the demo sheet');
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'GSN-scanner-demo-sheet.png'; a.click();
      window.open(url, '_blank', 'noopener');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="tickets-tab">
      <AdminRemovalDialog kind="ticket" role={role} target={removeTarget} token={token} onClose={() => setRemoveTarget(null)} onRemoved={id => { setTickets(rows => rows.filter(row => row.ticket_id !== id)); setReloadTick(value => value + 1); }} />
      {demo && (
        <Card className="mb-6 border-[hsl(var(--gs-accent-gold))]" data-testid="demo-tickets-card">
          <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2"><Ticket className="w-4 h-4" /> Demo tickets for client walkthroughs <Badge className={demo.ready ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'} data-testid="demo-tickets-badge">{demo.ready ? 'Ready' : 'Not created yet'}</Badge></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Six branded ticket QRs (member, non-member, child, student, already-scanned, cancelled), an invalid QR and two membership-card QRs (active = blue, expired = red), all tied to a hidden <em>DEMO - Scanner Training</em> event so they can never admit anyone to a real event. Open the sheet on one screen and scan it with the Staff Portal scanner on a phone. Reset between demos.</p>
              {demo.ready && demo.members && (
                <div className="flex flex-wrap gap-2 mt-2 text-xs" data-testid="demo-members-list">
                  {demo.members.map(m => <span key={m.membership_number} className="inline-flex items-center gap-1 rounded-md border px-2 py-1"><CreditCard className="w-3 h-3" /> {m.full_name} · {m.expected.split(' - ')[1]}{m.card_url && <a href={m.card_url} target="_blank" rel="noreferrer" className="underline ml-1" data-testid={`demo-member-card-${m.membership_number}`}>open card</a>}</span>)}
                </div>
              )}
              {demo.ready && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="demo-tickets-list">
                  {demo.tickets.map(t => <Badge key={t.ticket_id} variant="outline" className={`text-[10px] ${t.current_status !== t.start_status ? 'border-amber-500 text-amber-800' : ''}`} data-testid={`demo-ticket-${t.ticket_id}`}>{t.attendee_name.split(' (')[0]} · {t.current_status}{t.current_status !== t.start_status ? ' (scanned)' : ''}</Badge>)}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {!demo.ready && role === 'admin' && <Button size="sm" onClick={ensureDemo} data-testid="demo-tickets-create">Create demo set</Button>}
              {demo.ready && <Button size="sm" variant="outline" onClick={openSheet} data-testid="demo-tickets-sheet"><Download className="w-4 h-4 mr-1" /> Demo QR sheet</Button>}
              {demo.ready && <Button size="sm" variant="outline" onClick={resetDemo} data-testid="demo-tickets-reset"><RefreshCw className="w-4 h-4 mr-1" /> Reset demo tickets</Button>}
            </div>
          </CardContent>
        </Card>
      )}
      {role === 'admin' && <ComplimentaryPanel token={token} events={events} selectedEvent={selectedEvent} onIssued={refreshAfterIssue} />}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <h2 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>Tickets ({shownTickets.length}{range.preset !== 'all' ? ` of ${tickets.length}` : ''})</h2>
        <div className="flex items-center gap-2 flex-wrap max-w-full">
          <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)} className="rounded-lg border border-[hsl(var(--input))] px-3 py-2 text-sm bg-transparent max-w-[70vw]" data-testid="tickets-event-select">
            <option value="">All events</option>
            {events.map(e => <option key={e.event_id} value={e.event_id}>{e.title}</option>)}
          </select>
          <span className="text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap">Filter by</span>
          <Select value={dateField} onValueChange={setDateField}><SelectTrigger className="h-9 w-36" aria-label="Filter tickets by" data-testid="tickets-date-field"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="created_at">Booking date</SelectItem><SelectItem value="event_day">Event day</SelectItem></SelectContent></Select>
          <DateRangeFilter value={range} onChange={setRange} label="Dates" testId="tickets-date" />
          <Button variant="outline" size="sm" onClick={exportShown} disabled={!shownTickets.length} data-testid="tickets-export-filtered"><Download className="w-4 h-4 mr-1" /> Export list</Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!selectedEvent} title="Full attendee sheet for the selected event (server export)" data-testid="admin-export-attendees-button"><Download className="w-4 h-4 mr-1" /> Attendee sheet</Button>
        </div>
      </div>
      {summaryError ? <Card className="mb-4 border-destructive/40" data-testid="tickets-summary-error"><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3"><p className="text-sm" role="alert">{summaryError}</p><Button variant="outline" className="min-h-11" onClick={() => setReloadTick(value => value + 1)} data-testid="tickets-summary-retry">Retry counts</Button></CardContent></Card> : <>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3" data-testid="tickets-summary">
          {[
            ['Issued QR tickets', 'total_qr_tickets', 'total'],
            ['Regular member tickets', 'regular_member_tickets', 'member'],
            ['Regular non-member tickets', 'regular_non_member_tickets', 'nonmember'],
            ['Student Tickets (regular)', 'regular_student_tickets', 'student'],
            ['Used QR tickets', 'used_qr_tickets', 'used'],
            ['Complimentary QR tickets', 'complimentary_qr_tickets', 'complimentary'],
          ].map(([label, field, key]) => <Card key={key} data-testid={`tickets-summary-${key}`}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{label}</p>{ticketSummary ? <p className="text-2xl font-semibold tabular-nums mt-1" data-testid={`tickets-summary-${key}-value`}>{ticketSummary[field]}</p> : <Skeleton className="h-8 w-16 mt-1" />}</CardContent></Card>)}
        </div>
        <SeasonTicketsCard summary={ticketSummary} prefix="tickets-season" loading={!ticketSummary} className="mb-3" />
        <p className="mb-4 text-xs text-muted-foreground" data-testid="tickets-summary-explanation">Regular Member, Non-Member and Student counts are separate and exclude season passes. Season Member, Non-Member and Student passes count once per person and booking; their original daily QR tickets remain in this table and in check-in totals. Counts cover all matching records, not just the latest 500 rows. Complimentary totals are an overlapping subset.</p>
      </>}
      {listError ? <Card data-testid="tickets-list-error"><CardContent className="p-4 space-y-2"><p role="alert" className="text-sm text-destructive">{listError}</p><Button variant="outline" className="min-h-11" onClick={() => setReloadTick(value => value + 1)} data-testid="tickets-list-retry">Retry tickets</Button></CardContent></Card> : loading ? <Skeleton className="h-48 rounded-xl" data-testid="tickets-list-loading" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm gsn-operations-table" data-testid="admin-tickets-table">
            <thead><tr className="border-b">
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Ticket ID</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Name</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Type</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Ticket class</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Status</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Resend</th>
            </tr></thead>
            <tbody>
              {shownTickets.map(t => (
                <tr key={t.ticket_id} className="border-b hover:bg-[hsl(var(--muted))]">
                  <td className="p-3 font-mono text-xs">{t.ticket_id}</td>
                  <td className="p-3">
                    {t.attendee_name}
                    {t.customer_email && <div className="text-xs text-[hsl(var(--muted-foreground))]">{t.customer_email}</div>}
                  </td>
                  <td className="p-3">
                    <span className="capitalize">{t.ticket_type?.replace('_', ' ')}</span>
                    {t.is_season && <Badge variant="outline" className="ml-2 whitespace-normal" data-testid={`ticket-season-badge-${t.ticket_id}`}>Season — {t.is_student ? 'Student' : t.is_member ? 'Member' : 'Non-member'}</Badge>}
                    {t.complimentary && <Badge className="ml-2 bg-[hsl(var(--gs-accent-gold))]/20 text-[hsl(var(--gs-ink))] text-[10px]" data-testid={`ticket-comp-badge-${t.ticket_id}`}>Complimentary{t.comp_reason ? ` · ${t.comp_reason}` : ''}</Badge>}
                    {t.coupon_code && <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">Coupon {t.coupon_code}</div>}
                    {!t.complimentary && t.coupon_code && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">£{Number(t.price || 0).toFixed(2)} after discount</div>}
                  </td>
                  <td className="p-3" data-testid={`ticket-class-${t.ticket_id}`}><Badge variant="secondary" className="text-xs">{t.is_student ? 'Student' : t.is_member ? 'Member' : 'Non-member'}</Badge></td>
                  <td className="p-3"><Badge variant={t.status === 'valid' ? 'default' : t.status === 'used' ? 'secondary' : 'destructive'} className="text-xs">{t.status}</Badge></td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {role === 'admin' && <><BookingOperationsButton token={token} orderId={t.order_id} rowId={t.ticket_id} onChanged={refreshAfterIssue} /><Button variant="outline" size="sm" className="min-h-11 text-destructive border-destructive/30" onClick={() => setRemoveTarget(t)} data-testid={`ticket-remove-${t.ticket_id}`} aria-label={`Remove ticket for ${t.attendee_name || t.customer_name || 'attendee'}`}><Trash2 className="w-4 h-4 mr-1" />Remove</Button></>}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResend(t)}
                        disabled={resending === t.ticket_id || !t.customer_email}
                        title={t.customer_email ? `Re-email all passes for this order to ${t.customer_email}` : 'No buyer email on this order'}
                        data-testid={`resend-tickets-${t.ticket_id}`}
                      >
                        <Mail className="w-3.5 h-3.5 mr-1" />
                        {resending === t.ticket_id ? 'Sending...' : 'Resend'}
                      </Button>
                      {role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setOtherTicket(t); setOtherEmail(''); }}
                          disabled={resending === t.ticket_id}
                          title="Resend to a different email address (Admin only)"
                          data-testid={`resend-other-${t.ticket_id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {shownTickets.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="tickets-empty">{tickets.length ? 'No tickets in this date range' : 'No tickets found'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin-only: resend passes to a different email */}
      <Dialog open={!!otherTicket} onOpenChange={(open) => { if (!open) { setOtherTicket(null); setOtherEmail(''); } }}>
        <DialogContent className="max-w-md" data-testid="resend-other-dialog">
          <DialogHeader>
            <DialogTitle>Resend Passes to a Different Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Original buyer: <strong>{otherTicket?.customer_email || 'unknown'}</strong>.
              As Admin you can approve sending the QR passes for this order to another address
              (e.g. if the buyer lost access to their mailbox).
            </p>
            <div>
              <Label>Send passes to</Label>
              <Input
                type="email"
                value={otherEmail}
                onChange={e => setOtherEmail(e.target.value)}
                placeholder="different.email@example.com"
                data-testid="resend-other-email-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setOtherTicket(null); setOtherEmail(''); }}>Cancel</Button>
              <Button
                onClick={() => {
                  if (!otherEmail.trim() || !otherEmail.includes('@')) { toast.error('Enter a valid email address'); return; }
                  handleResend(otherTicket, otherEmail.trim());
                }}
                disabled={resending === otherTicket?.ticket_id}
                data-testid="resend-other-confirm-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                {resending === otherTicket?.ticket_id ? 'Sending...' : 'Approve & Resend'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Donations Tab
function DonationsTab({ token }) {
  const [allDonations, setAllDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(emptyDateRange());

  useEffect(() => {
    api.getAuth('/api/admin/donations', token).then(d => {
      setAllDonations(d.donations || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  // Date filter + totals recalculated for the chosen range
  const donations = allDonations.filter(d => inDateRange(d.created_at, range));
  const totals = donations.reduce((acc, d) => {
    const amt = Number(d.amount || 0);
    acc.total_all += amt; if (d.payment_status === 'paid') acc.total_paid += amt; return acc;
  }, { total_paid: 0, total_all: 0 });

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold flex-1" style={{fontFamily: 'Space Grotesk'}}>Donations ({donations.length}{range.preset !== 'all' ? ` of ${allDonations.length}` : ''})</h2>
        <DateRangeFilter value={range} onChange={setRange} label="Donation date" testId="donations-date" />
        <Button variant="outline" size="sm" disabled={!donations.length} onClick={() => downloadCsv(`donations_${rangeSlug(range)}`, [
          { key: 'donation_id', label: 'Reference' }, { key: 'donor_name', label: 'Donor', format: d => d.donor_name || 'Anonymous' }, { key: 'donor_email', label: 'Email' },
          { key: 'amount', label: 'Amount (GBP)', format: d => fmtMoney(d.amount) }, { key: 'payment_status', label: 'Status' }, { key: 'created_at', label: 'Date', format: d => fmtDateGB(d.created_at) },
        ], donations)} data-testid="donations-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Total Received (Paid) · <span data-testid="donations-range-label">{describeDateRange(range)}</span></div>
          <div className="text-3xl font-bold tabular-nums text-green-700" style={{fontFamily: 'Space Grotesk'}} data-testid="donations-total-paid">£{totals.total_paid.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Total incl. Pending</div>
          <div className="text-3xl font-bold tabular-nums" style={{fontFamily: 'Space Grotesk'}} data-testid="donations-total-all">£{totals.total_all.toFixed(2)}</div>
        </CardContent></Card>
      </div>
      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b">
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">ID</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Donor</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Amount</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Status</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Date</th>
            </tr></thead>
            <tbody>
              {donations.map(d => (
                <tr key={d.donation_id || d._id} className="border-b hover:bg-[hsl(var(--muted))]">
                  <td className="p-3 font-mono text-xs">{d.donation_id}</td>
                  <td className="p-3">{d.donor_name || 'Anonymous'}</td>
                  <td className="p-3 font-semibold tabular-nums" data-testid={`donation-amount-${d.donation_id}`}>£{Number(d.amount || 0).toFixed(2)}</td>
                  <td className="p-3"><Badge variant={d.payment_status === 'paid' ? 'default' : 'secondary'} className="text-xs">{d.payment_status}</Badge></td>
                  <td className="p-3 text-[hsl(var(--muted-foreground))]">{d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : '-'}</td>
                </tr>
              ))}
              {donations.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="donations-empty">{allDonations.length ? 'No donations in this date range' : 'No donations yet'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Saturday Club registrations (with payment status)
function SaturdayClubRegistrationsTab({ token }) {
  const [data, setData] = useState({ registrations: [], total_paid: 0, counts: { paid: 0, unpaid: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState(emptyDateRange());

  useEffect(() => {
    api.getAuth('/api/admin/saturday-club/registrations', token).then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const optLabel = { term: 'Per term', yearly: 'Yearly', custom: 'Custom' };
  const q = search.trim().toLowerCase();
  const inRange = (data.registrations || []).filter(r => inDateRange(r.created_at, range));
  const rows = inRange.filter(r => (filter === 'all' || (filter === 'paid' ? r.payment_status === 'paid' : r.payment_status !== 'paid'))
    && (!q || [r.name, r.email, r.child_name, r.registration_id, r.postcode].some(v => (v || '').toLowerCase().includes(q))));
  const totals = range.preset === 'all' ? { total_paid: Number(data.total_paid || 0), paid: data.counts?.paid || 0, unpaid: data.counts?.unpaid || 0 }
    : inRange.reduce((a, r) => { if (r.payment_status === 'paid') { a.paid += 1; a.total_paid += Number(r.amount_paid || 0); } else a.unpaid += 1; return a; }, { total_paid: 0, paid: 0, unpaid: 0 });
  const exportRows = () => downloadCsv(`saturday_club_${rangeSlug(range)}`, [
    { key: 'registration_id', label: 'Reference' }, { key: 'child_name', label: 'Child' }, { key: 'child_age', label: 'Age' }, { key: 'name', label: 'Parent / Guardian' },
    { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' }, { key: 'address', label: 'Address' }, { key: 'postcode', label: 'Postcode' },
    { key: 'payment_status', label: 'Payment' }, { key: 'amount_paid', label: 'Amount paid (GBP)', format: r => fmtMoney(r.amount_paid) }, { key: 'payment_option', label: 'Option', format: r => optLabel[r.payment_option] || r.payment_option || '' },
    { key: 'term_name', label: 'Term' }, { key: 'created_at', label: 'Registered', format: r => fmtDateGB(r.created_at) },
  ], rows);

  return (
    <div data-testid="saturday-club-registrations-tab">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold flex-1" style={{fontFamily: 'Space Grotesk'}}>Saturday Club Registrations ({rows.length}{range.preset !== 'all' || filter !== 'all' || q ? ` of ${(data.registrations || []).length}` : ''})</h2>
        <DateRangeFilter value={range} onChange={setRange} label="Registered" testId="saturday-club-date" />
        <Button variant="outline" size="sm" onClick={exportRows} disabled={!rows.length} data-testid="saturday-club-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Fees received (paid) · {describeDateRange(range)}</div>
          <div className="text-3xl font-bold tabular-nums text-green-700" style={{fontFamily: 'Space Grotesk'}} data-testid="saturday-club-total-paid">£{totals.total_paid.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Paid registrations</div>
          <div className="text-3xl font-bold tabular-nums" style={{fontFamily: 'Space Grotesk'}} data-testid="saturday-club-count-paid">{totals.paid}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Awaiting payment</div>
          <div className="text-3xl font-bold tabular-nums text-amber-700" style={{fontFamily: 'Space Grotesk'}} data-testid="saturday-club-count-unpaid">{totals.unpaid}</div>
        </CardContent></Card>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-1">
          {['all', 'paid', 'unpaid'].map(f => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} data-testid={`saturday-club-filter-${f}`} className="capitalize">{f}</Button>
          ))}
        </div>
        <Input placeholder="Search child, parent, email, reference or postcode" value={search} onChange={e => setSearch(e.target.value)} className="sm:max-w-md" data-testid="saturday-club-search" />
      </div>
      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b">
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Reference</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Child</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Parent / Guardian</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Address</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Payment</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Registered</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.registration_id} className="border-b hover:bg-[hsl(var(--muted))]" data-testid={`saturday-club-row-${r.registration_id}`}>
                  <td className="p-3 font-mono text-xs">{r.registration_id}{r.documents > 0 && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{r.documents} file{r.documents > 1 ? 's' : ''}</div>}</td>
                  <td className="p-3"><div className="font-medium">{r.child_name}</div>{r.child_age && <div className="text-xs text-[hsl(var(--muted-foreground))]">age {r.child_age}</div>}</td>
                  <td className="p-3"><div>{r.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{r.email}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{r.phone}</div></td>
                  <td className="p-3 text-xs">{r.address || '—'}{r.postcode && <div className="font-mono">{r.postcode}</div>}</td>
                  <td className="p-3">
                    <Badge variant={r.payment_status === 'paid' ? 'default' : 'secondary'} className="text-xs capitalize">{r.payment_status}</Badge>
                    {r.payment_status === 'paid' && <div className="text-xs mt-1 font-semibold tabular-nums">£{Number(r.amount_paid || 0).toFixed(2)} <span className="font-normal text-[hsl(var(--muted-foreground))]">{optLabel[r.payment_option] || ''}{r.term_name ? ` · ${r.term_name}` : ''}</span></div>}
                    {r.payment_status === 'pending' && r.amount_due != null && <div className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">£{Number(r.amount_due).toFixed(2)} checkout started</div>}
                  </td>
                  <td className="p-3 text-[hsl(var(--muted-foreground))]">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '-'}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No registrations found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Reminders Tab
function RemindersTab({ token }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAuth('/api/admin/reminders', token).then(d => setReminders(d.reminders || [])).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>Event Reminders ({reminders.length})</h2>
        <Badge variant="outline" className="text-xs">SMS/WhatsApp: Coming Soon</Badge>
      </div>
      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b">
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Event</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Phone</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Name</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Status</th>
              <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Channels</th>
            </tr></thead>
            <tbody>
              {reminders.map(r => (
                <tr key={r.reminder_id || r._id} className="border-b hover:bg-[hsl(var(--muted))]">
                  <td className="p-3">{r.event_title}</td>
                  <td className="p-3">{r.phone_number}</td>
                  <td className="p-3">{r.name || '-'}</td>
                  <td className="p-3"><Badge variant="secondary" className="text-xs">{r.status}</Badge></td>
                  <td className="p-3">{r.send_via?.join(', ')}</td>
                </tr>
              ))}
              {reminders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No reminders set</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Email Delivery status card (own-mailbox SMTP — no third-party marketing provider)
function EmailDeliveryCard({ token, role }) {
  const [status, setStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [dailyLimit, setDailyLimit] = useState('');
  const [batchSize, setBatchSize] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.getAuth('/api/admin/email-settings', token);
      setStatus(data);
      if (data.pacing) {
        setDailyLimit(String(data.pacing.daily_limit ?? 150));
        setBatchSize(String(data.pacing.batch_size ?? 15));
      }
    } catch { /* non-critical */ }
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleTestSend = async () => {
    if (!testEmail.trim()) { toast.error('Enter an email address for the test'); return; }
    setBusy(true);
    try {
      const res = await api.postAuth('/api/admin/email-test', { to: testEmail }, token);
      toast.success(res.message);
      setTestEmail('');
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const handleProcessNow = async () => {
    setBusy(true);
    try {
      const res = await api.postAuth('/api/admin/email-queue/process', {}, token);
      toast.success(`Sent ${res.sent} email(s)${res.failed ? `, ${res.failed} failed` : ''}`);
      loadStatus();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const handleRetryFailed = async () => {
    setBusy(true);
    try {
      const res = await api.postAuth('/api/admin/email-queue/retry-failed', {}, token);
      toast.success(`${res.requeued} failed email(s) re-queued`);
      loadStatus();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const handleSavePacing = async () => {
    const dl = parseInt(dailyLimit, 10);
    const bs = parseInt(batchSize, 10);
    if (!dl || dl < 1) { toast.error('Daily limit must be at least 1'); return; }
    if (!bs || bs < 1 || bs > 100) { toast.error('Per-minute batch must be 1-100'); return; }
    setBusy(true);
    try {
      await api.putAuth('/api/admin/settings', { email_daily_limit: dl, email_batch_size: bs }, token);
      toast.success('Send pacing saved');
      loadStatus();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const configured = status?.smtp?.configured;
  const q = status?.queue || {};

  return (
    <Card data-testid="email-delivery-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email Delivery (Newsletters & Ticket Copies)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--muted)/0.5)]" data-testid="email-delivery-status">
          {configured ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">Sending Active</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Via {status?.smtp?.host} as {status?.smtp?.from_email} — queued emails send automatically every minute.
                </p>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">Not Connected Yet — Emails Queue Safely</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Uses your own mailbox (e.g. info@gujaratsamajnottingham.com) via SMTP — no third-party marketing provider,
                  your subscriber data never leaves this system. Ticket copies, newsletters and confirmations wait in the queue
                  and send automatically the moment SMTP details are added to the server.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <Badge variant="outline" data-testid="email-queue-queued-badge">Queued: {q.queued ?? 0}</Badge>
          <Badge variant="outline" className="border-green-500/60 text-green-700" data-testid="email-queue-sent-badge">Sent: {q.sent ?? 0}</Badge>
          <Badge variant="outline" className="border-red-500/60 text-red-700" data-testid="email-queue-failed-badge">Failed: {q.failed ?? 0}</Badge>
          {(q.newsletters_queued ?? 0) > 0 && (
            <Badge variant="outline" className="border-blue-500/60 text-blue-700" data-testid="email-queue-newsletters-badge">Newsletters waiting: {q.newsletters_queued}</Badge>
          )}
        </div>

        {/* Send pacing — protects your mailbox's daily limits */}
        <div className="p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]" data-testid="email-pacing-section">
          <Label className="font-semibold">Send Pacing (protects against bounces)</Label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 mb-3">
            Newsletter blasts drip out gently to stay within your mailbox's daily sending limits.
            Ticket copies and confirmations always send immediately — they are never held back.
            {status?.pacing && <> Today: <strong>{status.pacing.newsletters_sent_today ?? 0} / {status.pacing.daily_limit}</strong> newsletter emails sent.</>}
          </p>
          {role === 'admin' ? (
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
              <div>
                <Label className="text-xs">Max newsletters per day</Label>
                <Input type="number" min="1" value={dailyLimit} onChange={e => setDailyLimit(e.target.value)} className="w-36" data-testid="email-pacing-daily-input" />
              </div>
              <div>
                <Label className="text-xs">Per minute</Label>
                <Input type="number" min="1" max="100" value={batchSize} onChange={e => setBatchSize(e.target.value)} className="w-28" data-testid="email-pacing-batch-input" />
              </div>
              <Button onClick={handleSavePacing} disabled={busy} variant="outline" size="sm" data-testid="email-pacing-save-button">Save Pacing</Button>
            </div>
          ) : (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Limits: {status?.pacing?.daily_limit ?? 150}/day, {status?.pacing?.batch_size ?? 15}/minute (Admin can change)</p>
          )}
        </div>

        {role === 'admin' && (
          <>
            <Separator />
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="Send a test email to..."
                className="sm:w-72"
                data-testid="email-test-input"
              />
              <Button onClick={handleTestSend} disabled={busy} variant="outline" data-testid="email-test-send-button">
                <Send className="w-4 h-4 mr-2" /> Send Test
              </Button>
              <Button onClick={handleProcessNow} disabled={busy} variant="outline" data-testid="email-queue-process-button">
                <RefreshCw className="w-4 h-4 mr-2" /> Send Queued Now
              </Button>
              {(q.failed ?? 0) > 0 && (
                <Button onClick={handleRetryFailed} disabled={busy} variant="outline" data-testid="email-queue-retry-button">
                  Retry Failed
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Settings Tab - Admin CMS
function SettingsTab({ token, role = 'admin' }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeMasked, setStripeMasked] = useState(null);
  const [stripeMode, setStripeMode] = useState('live');
  const [stripeLiveMasked, setStripeLiveMasked] = useState(null);
  const [stripeTestMasked, setStripeTestMasked] = useState(null);
  const [modeDialog, setModeDialog] = useState(null);   // 'live' | 'test'
  const [modeTick, setModeTick] = useState(false);

  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [allowUnverifiedBooking, setAllowUnverifiedBooking] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const [autoTheme, setAutoTheme] = useState(true);
  const [stripeKey, setStripeKey] = useState('');
  const [showStripeKey, setShowStripeKey] = useState(false);

  const [siteName, setSiteName] = useState('Gujarat Samaj Nottingham');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [charityNumber, setCharityNumber] = useState('513936');
  // Organisation (single source of truth for contact/social details across the public site)
  const [org, setOrg] = useState({ org_address: '', org_map_link: '', whatsapp_number: '', whatsapp_enabled: true, whatsapp_message: '', facebook_url: '', instagram_url: '', youtube_url: '', office_hours: '' });

  const loadSettings = useCallback(async () => {
    try {
      const data = await api.getAuth('/api/admin/settings', token);
      setSettings(data.settings);
      setStripeConfigured(data.stripe_configured);
      setStripeMasked(data.stripe_key_masked);
      setStripeMode(data.stripe_mode || 'live');
      setStripeLiveMasked(data.stripe_live_key_masked || null);
      setStripeTestMasked(data.stripe_test_key_masked || null);

      // Populate form
      setPaymentsEnabled(data.settings.payments_enabled !== false);
      setAllowUnverifiedBooking(data.settings.allow_unverified_ticket_booking === true);
      setPaymentProvider(data.settings.payment_provider || 'stripe');
      setAutoTheme(data.settings.auto_theme_enabled !== false);
      setSiteName(data.settings.site_name || 'Gujarat Samaj Nottingham');
      setContactEmail(data.settings.contact_email || '');
      setContactPhone(data.settings.contact_phone || '');
      setCharityNumber(data.settings.charity_number || '513936');
      const st = data.settings;
      setOrg({
        org_address: st.org_address ?? '28 Handel Street, Nottingham NG3 1JE',
        org_map_link: st.org_map_link ?? 'https://maps.google.com/?q=28+Handel+Street+Nottingham+NG3+1JE',
        whatsapp_number: st.whatsapp_number ?? '+447502076709',
        whatsapp_enabled: st.whatsapp_enabled !== false,
        whatsapp_message: st.whatsapp_message ?? 'Hello Gujarat Samaj Nottingham, I have a question about ',
        facebook_url: st.facebook_url ?? 'https://www.facebook.com/share/1UaeYDTuzR/?mibextid=wwXIfr',
        instagram_url: st.instagram_url ?? 'https://www.instagram.com/gujaratsamajnotts?igsi=MWtrNzFwc2I5NWRjYg%3D%3D&utm_source=qr',
        youtube_url: st.youtube_url ?? '',
        office_hours: st.office_hours ?? '',
      });
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.putAuth('/api/admin/settings', {
        payments_enabled: paymentsEnabled,
        allow_unverified_ticket_booking: allowUnverifiedBooking,
        payment_provider: paymentProvider,
        auto_theme_enabled: autoTheme,
        site_name: siteName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        charity_number: charityNumber,
        ...org,
      }, token);
      toast.success('Settings saved — contact details updated across the website');
      loadSettings();
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStripeKey = async () => {
    if (!stripeKey.trim()) {
      toast.error('Please enter a Stripe API key');
      return;
    }

    if (stripeKey.trim().startsWith('pk_')) {
      toast.error('That is the Publishable key (pk_…). Please paste the SECRET key — it starts with sk_test_ or sk_live_ (Stripe Dashboard → Developers → API keys → Secret key → Reveal).');
      return;
    }
    if (!stripeKey.trim().startsWith('sk_test_') && !stripeKey.trim().startsWith('sk_live_')) {
      toast.error('Invalid Stripe key format — the secret key starts with sk_test_ or sk_live_');
      return;
    }

    setSaving(true);
    try {
      const res = await api.putAuth('/api/admin/settings/stripe', {
        stripe_secret_key: stripeKey.trim()
      }, token);
      toast.success(res.message || 'Stripe key saved');
      setStripeKey('');
      setShowStripeKey(false);
      loadSettings();
    } catch (e) {
      toast.error(e.message || 'Failed to update Stripe key');
    } finally {
      setSaving(false);
    }
  };

  const switchMode = async () => {
    try {
      const res = await api.putAuth('/api/admin/settings/stripe-mode', { mode: modeDialog, confirm: true }, token);
      toast.success(res.message);
      setModeDialog(null); setModeTick(false); loadSettings();
    } catch (e) { toast.error(e.message || 'Could not switch mode'); }
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-32 bg-[hsl(var(--muted))] rounded-xl" />)}
    </div>;
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1" style={{fontFamily: 'Space Grotesk'}}>Admin Settings</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Manage site configuration, payments, and integrations</p>
      </div>

      {/* Payments & Stripe */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Payments & Stripe Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Payments Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
            <div>
              <Label className="text-base font-semibold">Enable Payments Site-Wide</Label>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Control if donations, memberships, and tickets can be purchased</p>
            </div>
            <button
              onClick={() => setPaymentsEnabled(!paymentsEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${paymentsEnabled ? 'bg-[hsl(var(--gs-success))]' : 'bg-[hsl(var(--muted))]'}`}
              data-testid="admin-settings-payments-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${paymentsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <Separator />

          {/* Payment Provider */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Payment Gateway Provider</Label>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Currently Stripe is fully integrated. Other gateways can be connected in future without rebuilding the site.</p>
            <select value={paymentProvider} onChange={e => setPaymentProvider(e.target.value)}
              className="w-full sm:w-72 rounded-lg border border-[hsl(var(--input))] px-3 py-2 text-sm bg-transparent"
              data-testid="admin-settings-payment-provider-select" disabled={role !== 'admin'}>
              <option value="stripe">Stripe (active)</option>
              <option value="paypal">PayPal (coming soon)</option>
              <option value="square">Square (coming soon)</option>
              <option value="gocardless">GoCardless (coming soon)</option>
            </select>
            {paymentProvider !== 'stripe' && (
              <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> This gateway is not yet connected — payments will continue via Stripe until integration is completed.</p>
            )}
          </div>

          <Separator />

          {/* Stripe mode: LIVE vs TEST */}
          <div className={`p-4 rounded-lg border ${stripeMode === 'test' ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`} data-testid="admin-settings-stripe-mode-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">Payment mode <Badge className={stripeMode === 'test' ? 'bg-amber-600 text-white' : 'bg-green-700 text-white'} data-testid="admin-settings-stripe-mode-badge">{stripeMode === 'test' ? 'TEST — no real charges' : 'LIVE — real payments'}</Badge></Label>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  {stripeMode === 'test' ? 'Checkouts and refunds use Stripe test cards (e.g. 4242 4242 4242 4242). Switch back to LIVE before members pay for real.' : 'Real cards are charged. Switch to TEST to safely try checkout and refunds end-to-end.'}
                </p>
                <div className="text-xs mt-2 space-x-3 font-mono">
                  <span>Live key: {stripeLiveMasked || <em className="not-italic text-red-700">not set</em>}</span>
                  <span>Test key: {stripeTestMasked || <em className="not-italic text-amber-800">not set</em>}</span>
                </div>
              </div>
              {role === 'admin' && (
                <Button variant="outline" onClick={() => { setModeDialog(stripeMode === 'test' ? 'live' : 'test'); setModeTick(false); }} disabled={stripeMode !== 'test' && !stripeTestMasked} data-testid="admin-settings-stripe-mode-switch">
                  {stripeMode === 'test' ? 'Switch to LIVE' : (stripeTestMasked ? 'Switch to TEST mode' : 'Add a test key to enable TEST mode')}
                </Button>
              )}
            </div>
          </div>

          {/* Stripe Key Status */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Stripe API Key (currently in use)</Label>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--muted)/0.5)]">
              {stripeConfigured ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Key Configured</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{stripeMasked}</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">No Stripe Key Configured</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Payments will not work until a key is added</p>
                  </div>
                </>
              )}
            </div>

            {!showStripeKey ? (
              <Button onClick={() => setShowStripeKey(true)} variant="outline" data-testid="admin-settings-stripe-update-button">
                <Edit className="w-4 h-4 mr-2" />
                {stripeConfigured ? 'Update Stripe Key' : 'Add Stripe Key'}
              </Button>
            ) : (
              <div className="space-y-3 p-4 rounded-lg border border-[hsl(var(--border))]">
                <Label>Stripe Secret Key</Label>
                <Input
                  type="password"
                  value={stripeKey}
                  onChange={e => setStripeKey(e.target.value)}
                  placeholder="sk_test_... or sk_live_..."
                  data-testid="admin-settings-stripe-key-input"
                />
                <div className="flex gap-2">
                  <Button onClick={handleUpdateStripeKey} disabled={saving} data-testid="admin-settings-stripe-save-button">
                    {saving ? 'Saving...' : 'Save Key'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowStripeKey(false); setStripeKey(''); }}>
                    Cancel
                  </Button>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Keys are stored server-side only and apply immediately. A <code>sk_test_</code> key is saved to the TEST slot and a <code>sk_live_</code> key to the LIVE slot — adding a test key never replaces your live key.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Event ticket booking verification (admin only) */}
      {role === 'admin' && (
      <Card data-testid="admin-settings-ticket-verification-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            Event Ticket Booking Verification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
            <div>
              <Label className="text-base font-semibold">Allow booking by name + GS number match (unverified)</Label>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">When ON, a member whose typed name matches their GS membership number can book a member ticket even without completing the online claim. Use this only while you can verify attendees in person (e.g. at the gate), then switch it back OFF. The one member-ticket / one season-ticket per GS number per event rule still applies.</p>
            </div>
            <button
              type="button" role="switch" aria-checked={allowUnverifiedBooking}
              onClick={() => setAllowUnverifiedBooking(!allowUnverifiedBooking)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${allowUnverifiedBooking ? 'bg-[hsl(var(--gs-success))]' : 'bg-[hsl(var(--muted))]'}`}
              data-testid="admin-settings-unverified-booking-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowUnverifiedBooking ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {allowUnverifiedBooking && <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="admin-settings-unverified-booking-warning"><AlertTriangle className="w-3 h-3" /> Currently ON — unclaimed/unverified members can book member tickets by name + GS number match. Remember to switch this OFF once you no longer need it.</p>}
          <p className="text-xs text-[hsl(var(--muted-foreground))]">You can also mark an individual member as personally verified from Existing Member Migration → Claim-access follow-up, which grants that one GS number ticket access permanently without needing this switch.</p>
        </CardContent>
      </Card>
      )}

      <Dialog open={!!modeDialog} onOpenChange={(o) => { if (!o) { setModeDialog(null); setModeTick(false); } }}>
        <DialogContent data-testid="admin-settings-stripe-mode-dialog">
          <DialogHeader>
            <DialogTitle>Switch payments to {modeDialog === 'test' ? 'TEST' : 'LIVE'} mode?</DialogTitle>
            <DialogDescription>
              {modeDialog === 'test'
                ? 'While in TEST mode nobody can make a real payment — every checkout on the public site will use Stripe test mode. Use this only while verifying flows, then switch back.'
                : 'Real cards will be charged from now on. Make sure you have finished testing.'}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="checkbox" className="mt-1" checked={modeTick} onChange={e => setModeTick(e.target.checked)} data-testid="admin-settings-stripe-mode-confirm" /><span>I understand and want to switch to {modeDialog === 'test' ? 'TEST' : 'LIVE'} mode.</span></label>
          <DialogFooter><Button variant="outline" onClick={() => setModeDialog(null)}>Cancel</Button><Button onClick={switchMode} disabled={!modeTick} className={modeDialog === 'live' ? 'bg-green-700 text-white' : 'bg-amber-600 text-white'} data-testid="admin-settings-stripe-mode-submit">Switch to {modeDialog === 'test' ? 'TEST' : 'LIVE'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Delivery (own-mailbox SMTP) */}
      <EmailDeliveryCard token={token} role={role} />

      {/* Amazon SES delivery (admin + treasurer view; admin controls) */}
      <SesDeliveryCard token={token} role={role} />

      {/* Locked, one-click controlled SES test workflow (admin only) */}
      <SesTestWorkflowCard token={token} role={role} />

      {/* Weekly digest email to staff (admin only) */}
      {role === 'admin' && <WeeklyDigestCard token={token} />}

      {/* Site Settings (admin only) */}
      {role === 'admin' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Site Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
            <div>
              <Label className="font-semibold">Automatic Festival Themes</Label>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Diwali theme runs 10 days before until 10 days after Diwali (with Nutan Varsha Abhinandan wishes after Diwali day). Holi theme on Holika Dahan + Dhuleti. Uttarayan kite theme 12–15 January. A manually selected theme always overrides.</p>
            </div>
            <button onClick={() => setAutoTheme(!autoTheme)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${autoTheme ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
              data-testid="admin-settings-auto-theme-toggle">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoTheme ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {role === 'admin' && <div className="mb-6"><FestivalPhotosPanel token={token} /></div>}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Site Name</Label>
              <Input
                value={siteName}
                onChange={e => setSiteName(e.target.value)}
                placeholder="Gujarat Samaj Nottingham"
                data-testid="admin-settings-site-name-input"
              />
            </div>
            <div>
              <Label>Charity Number</Label>
              <Input
                value={charityNumber}
                onChange={e => setCharityNumber(e.target.value)}
                placeholder="513936"
                data-testid="admin-settings-charity-number-input"
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
                placeholder="info@gujaratsamajnottingham.com"
                data-testid="admin-settings-contact-email-input"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                type="tel"
                value={contactPhone}
                onChange={e => setContactPhone(e.target.value)}
                placeholder="+44 123 456 7890"
                data-testid="admin-settings-contact-phone-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Organisation contact & social (admin only) — single source of truth used by footer, contact page and WhatsApp button */}
      {role === 'admin' && (
      <Card data-testid="admin-settings-organisation-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Organisation — Contact & Social
          </CardTitle>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">These details appear in the footer, on the Contact page and in the WhatsApp chat button. Change them once here and every page updates.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Address</Label><Input value={org.org_address} onChange={e => setOrg({ ...org, org_address: e.target.value })} placeholder="28 Handel Street, Nottingham NG3 1JE" data-testid="admin-settings-org-address-input" /></div>
            <div className="sm:col-span-2"><Label>Map link</Label><Input value={org.org_map_link} onChange={e => setOrg({ ...org, org_map_link: e.target.value })} placeholder="https://maps.google.com/?q=…" data-testid="admin-settings-org-map-input" /></div>
            <div><Label>Office / opening hours (optional)</Label><Input value={org.office_hours} onChange={e => setOrg({ ...org, office_hours: e.target.value })} placeholder="e.g. Saturdays 10am–1pm" data-testid="admin-settings-org-hours-input" /></div>
            <div><Label>Facebook URL</Label><Input value={org.facebook_url} onChange={e => setOrg({ ...org, facebook_url: e.target.value })} placeholder="https://www.facebook.com/…" data-testid="admin-settings-org-facebook-input" /></div>
            <div><Label>Instagram URL</Label><Input value={org.instagram_url} onChange={e => setOrg({ ...org, instagram_url: e.target.value })} placeholder="https://www.instagram.com/…" data-testid="admin-settings-org-instagram-input" /></div>
            <div><Label>YouTube URL (optional)</Label><Input value={org.youtube_url} onChange={e => setOrg({ ...org, youtube_url: e.target.value })} placeholder="https://www.youtube.com/@…" data-testid="admin-settings-org-youtube-input" /></div>
          </div>
          <Separator />
          <div className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]">
            <div>
              <Label className="font-semibold">WhatsApp click-to-chat button</Label>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Shows a floating “Chat with us” button on every public page that opens WhatsApp with your number.</p>
            </div>
            <button type="button" onClick={() => setOrg({ ...org, whatsapp_enabled: !org.whatsapp_enabled })} role="switch" aria-checked={org.whatsapp_enabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${org.whatsapp_enabled ? 'bg-[hsl(var(--gs-success))]' : 'bg-[hsl(var(--muted))]'}`}
              data-testid="admin-settings-whatsapp-toggle">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${org.whatsapp_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>WhatsApp number (international format)</Label><Input value={org.whatsapp_number} onChange={e => setOrg({ ...org, whatsapp_number: e.target.value })} placeholder="+447502076709" data-testid="admin-settings-whatsapp-number-input" /></div>
            <div><Label>Pre-filled message</Label><Input value={org.whatsapp_message} onChange={e => setOrg({ ...org, whatsapp_message: e.target.value })} placeholder="Hello Gujarat Samaj Nottingham…" data-testid="admin-settings-whatsapp-message-input" /></div>
          </div>
          {org.whatsapp_number && (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Preview link: <a className="underline" target="_blank" rel="noreferrer" href={`https://wa.me/${org.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(org.whatsapp_message || '')}`} data-testid="admin-settings-whatsapp-preview">wa.me/{org.whatsapp_number.replace(/[^0-9]/g, '')}</a></p>
          )}
        </CardContent>
      </Card>
      )}

      {/* Save Button */}
      {role === 'admin' && (
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-[hsl(var(--gs-navy))] text-white"
          data-testid="admin-settings-save-all-button"
        >
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
      )}
    </div>
  );
}

// File Manager Tab - Enhanced Version
function FileManagerTab({ token, role }) {
  const canDelete = ['admin', 'chairman'].includes(role);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('gallery');

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAuth(`/api/admin/files?category=${selectedCategory}`, token);
      setFiles(data.files || []);
    } catch (e) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, token]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size based on type
    const ext = file.name.split('.').pop().toLowerCase();
    const isVideo = ['mp4', 'mov', 'avi', 'webm'].includes(ext);
    const isPDF = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext);

    const maxSize = isVideo ? 1024 * 1024 * 1024 : isPDF ? 10 * 1024 * 1024 : 30 * 1024 * 1024;

    if (file.size > maxSize) {
      const limit = isVideo ? '1GB' : isPDF ? '10MB' : '30MB';
      toast.error(`File too large. Max size for ${ext.toUpperCase()}: ${limit}`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload?category=${selectedCategory}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }

      toast.success(`${file.name} uploaded successfully!`);
      loadFiles();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;

    try {
      await api.deleteAuth(`/api/admin/files/${fileId}`, token);
      toast.success('File deleted');
      loadFiles();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const copyUrl = (fileId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied!');
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1" style={{fontFamily: 'Space Grotesk'}}>File Manager</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Upload and manage images, videos, and documents</p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-[hsl(var(--gs-navy))]">{files.length}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Total Files</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-blue-600">{formatSize(files.reduce((sum, f) => sum + (f.size || 0), 0))}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Total Size</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-green-600">{files.filter(f => f.content_type?.startsWith('image/')).length}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Images</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-purple-600">{files.filter(f => f.content_type?.startsWith('video/')).length}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Videos</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" />Upload Files</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">Category</Label>
            <div className="flex gap-2 flex-wrap">
              {['gallery', 'events', 'sponsors', 'general'].map(cat => (
                <Button key={cat} onClick={() => setSelectedCategory(cat)} variant={selectedCategory === cat ? 'default' : 'outline'} size="sm" className={selectedCategory === cat ? 'bg-[hsl(var(--gs-navy))]' : ''}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="cursor-pointer">
              <input type="file" className="hidden" accept="image/*,.heic,.heif,video/*,.pdf" onChange={handleUpload} disabled={uploading} />
              <Button disabled={uploading} className="w-full bg-[hsl(var(--gs-navy))]" type="button" as="span"><Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : `Upload to ${selectedCategory}`}</Button>
            </Label>
            <div className="mt-2 p-3 bg-[hsl(var(--muted)/0.5)] rounded-lg">
              <p className="text-xs font-semibold mb-1">Size Limits:</p>
              <ul className="text-xs space-y-0.5"><li>• <strong>Images</strong>: Max 30MB (iPhone HEIC converted automatically)</li><li>• <strong>Videos</strong>: Max 1GB</li><li>• <strong>PDFs</strong>: Max 10MB</li></ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Files ({files.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--gs-navy))] mx-auto"></div></div> : files.length === 0 ? <div className="text-center py-12"><Upload className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-3" /><p className="text-sm text-[hsl(var(--muted-foreground))]">No files yet</p></div> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map(file => {
                const isVideo = file.content_type?.startsWith('video/');
                const isImage = file.content_type?.startsWith('image/');
                const isPDF = file.content_type === 'application/pdf';
                return (
                  <Card key={file.file_id} className="overflow-hidden">
                    <div className="aspect-video bg-[hsl(var(--muted)/0.3)] relative">
                      {isImage && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.file_id}`} alt={file.original_filename} className="w-full h-full object-cover" />}
                      {isVideo && <div className="w-full h-full flex items-center justify-center"><video className="w-full h-full" controls><source src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.file_id}`} /></video></div>}
                      {isPDF && <div className="w-full h-full flex items-center justify-center"><FileText className="w-16 h-16 text-[hsl(var(--muted-foreground))]" /></div>}
                      <Badge className="absolute top-2 right-2 bg-black/60 text-white">{file.category}</Badge>
                    </div>
                    <CardContent className="p-4">
                      <p className="text-sm font-medium truncate mb-1">{file.original_filename}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{formatSize(file.size)} • {new Date(file.created_at).toLocaleDateString()}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => copyUrl(file.file_id)} className="flex-1"><FileText className="w-3 h-3 mr-1" />Copy URL</Button>
                        {canDelete && <Button variant="outline" size="sm" onClick={() => handleDelete(file.file_id)} className="text-red-600" aria-label="Delete file"><Trash2 className="w-3 h-3" /></Button>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
